import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  LiveKitRoom,
  useChat,
  useConnectionState,
  useLocalParticipant,
  useParticipants,
  useRoomContext,
  useTracks,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { ConnectionState, DisconnectReason, LogLevel, RoomEvent, setLogLevel, Track } from "livekit-client";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  Copy,
  Download,
  FileText,
  ImageIcon,
  Library,
  LogIn,
  LogOut,
  MessageSquare,
  Mic,
  MicOff,
  MoreVertical,
  Paperclip,
  PhoneOff,
  Radio,
  Search,
  Send,
  ShieldCheck,
  Square,
  UserCheck,
  UserMinus,
  UserPlus,
  UserRound,
  Users,
  UserX,
  Video,
  VideoOff,
  X,
} from "lucide-react";
import {
  closeLiveInterviewRoom,
  createLiveInterviewCandidateInvite,
  fetchInterviewerContextByToken,
  getLiveInterviewInviteJoinRequest,
  getLiveInterviewChatAttachmentDownload,
  getLiveInterviewRecordingDownloadUrl,
  getLiveInterviewTranscriptPdfUrl,
  getLiveInterviewRoom,
  getLiveInterviewJoinRequests,
  leaveLiveInterviewParticipant,
  muteAllLiveInterviewCandidates,
  muteLiveInterviewCandidates,
  muteLiveInterviewParticipantCandidate,
  requestLiveInterviewInviteJoin,
  getLiveInterviewJoinEligibility,
  requestLiveInterviewJoinToken,
  removeLiveInterviewParticipant,
  respondLiveInterviewJoinRequest,
  startLiveInterviewRecording,
  stopLiveInterviewRecording,
  updateLiveInterviewRoomSettings,
  uploadLiveInterviewChatAttachment,
} from "../api/liveInterviewApi";
import LiveTranscriptCapture from "../components/LiveTranscriptCapture";
import { LiveInterviewVideoConference } from "../components/LiveInterviewVideoConference";
import { LiveInterviewMeetingRecordingBar } from "../components/LiveInterviewMeetingRecordingBar";
import { LiveInterviewParticipantTile } from "../components/LiveInterviewParticipantTile";
import PreJoin from "../components/PreJoin";
import TranscriptPanel from "../components/TranscriptPanel";
import LiveInterviewSettingsMenu from "../components/LiveInterviewSettingsMenu";
import InterviewerCandidatePresenceWatcher from "../components/InterviewerCandidatePresenceWatcher";
import InterviewerFeedbackChoiceModal from "../components/InterviewerFeedbackChoiceModal";
import InterviewerFeedbackWizard from "../components/InterviewerFeedbackWizard";
import {
  canRejoinDisconnectedSession,
  canShowStaffRejoinLobby,
  getLobbyRejoinCopy,
  getReconnectBannerMessage,
  REMOVED_FROM_INTERVIEW_MESSAGE,
} from "../utils/liveInterviewRejoinUi";
import {
  cancelInterviewerFeedbackAutoEmail,
  deferInterviewerFeedback,
  prepareInterviewerFeedback,
  scheduleInterviewerFeedbackAutoEmail,
} from "../api/interviewerFeedbackApi";
import {
  getParticipantInitials,
  isGenericParticipantLabel,
  isValidGuestDisplayName,
  normalizeGuestDisplayName,
  isOpenJoinLiveMeetRoom,
  resolveParticipantDisplayName,
  isEmailLike,
} from "../utils/liveInterviewDisplayUtils";
import {
  parseUnmuteRequestDataPayload,
  publishUnmuteRequest,
  UNMUTE_REQUEST_TOPIC,
} from "../utils/liveInterviewUnmuteRequest";
import {
  applyAudioOutputToLiveKitRoom,
  persistAudioOutputDeviceId,
  readStoredAudioOutputDeviceId,
} from "../utils/liveInterviewAudioOutput";
import { FEEDBACK_LINK_VALIDITY_LABEL } from "../utils/feedbackLinkValidity";
import "../LiveInterview.css";

// Silence LiveKit's chatty info-level logger (it spams things like
// "already connected to room ..." on every parent re-render). We keep
// warnings/errors so genuine LiveKit problems are still visible.
try {
  setLogLevel(LogLevel.warn);
} catch {
  // Older LiveKit builds may not expose setLogLevel — not fatal.
}

const normalizeEmail = (value) => (value || "").trim().toLowerCase();

const LIVE_INTERVIEW_USER_EMAIL_KEYS = [
  "admin_email",
  "employer_email",
  "recruiter_email",
  "company_email",
  "organizer_email",
];
const FEEDBACK_EMAIL_RETRY_STORAGE_KEY =
  "searchjobai.liveInterview.feedbackEmailRetry";
const INTERVIEWER_DISPLAY_NAME_STORAGE_PREFIX =
  "searchjobai.liveInterview.interviewerDisplayName.v1";

const getInterviewerDisplayNameStorageKey = (roomId, interviewerEmail) => {
  const safeRoomId = String(roomId || "").trim();
  const safeInterviewerEmail = normalizeEmail(interviewerEmail);
  if (!safeRoomId || !safeInterviewerEmail) return "";
  return `${INTERVIEWER_DISPLAY_NAME_STORAGE_PREFIX}:${encodeURIComponent(safeRoomId)}:${encodeURIComponent(safeInterviewerEmail)}`;
};

const readInterviewerDisplayName = (storageKey) => {
  if (typeof window === "undefined" || !storageKey) return "";
  try {
    return normalizeGuestDisplayName(window.sessionStorage.getItem(storageKey) || "");
  } catch {
    return "";
  }
};

const writeInterviewerDisplayName = (storageKey, displayName) => {
  if (typeof window === "undefined" || !storageKey) return;
  try {
    window.sessionStorage.setItem(storageKey, displayName);
  } catch {
    // Best-effort only. Joining must still work if browser storage is unavailable.
  }
};

const getFeedbackEmailRetryScope = (bookingId, roomId) => {
  const safeBookingId = String(bookingId || "").trim();
  if (safeBookingId) return safeBookingId;
  const safeRoomId = String(roomId || "").trim();
  return safeRoomId ? `room:${safeRoomId}` : "";
};

const buildFeedbackEmailRetryKey = (bookingId, roomId, interviewerEmail) => {
  const scope = getFeedbackEmailRetryScope(bookingId, roomId);
  if (!scope) return "";
  return `${scope}::${normalizeEmail(interviewerEmail)}`;
};

const readFeedbackEmailRetryEntries = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(FEEDBACK_EMAIL_RETRY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeFeedbackEmailRetryEntries = (entries) => {
  if (typeof window === "undefined") return;
  try {
    if (!entries?.length) {
      window.localStorage.removeItem(FEEDBACK_EMAIL_RETRY_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      FEEDBACK_EMAIL_RETRY_STORAGE_KEY,
      JSON.stringify(entries)
    );
  } catch {
    // Best-effort only.
  }
};

const upsertFeedbackEmailRetryEntry = (payload) => {
  const bookingId = String(payload?.bookingId || payload?.booking_id || "").trim();
  const roomId = String(payload?.roomId || payload?.room_id || "").trim();
  const interviewerEmail = String(
    payload?.interviewerEmail || payload?.interviewer_email || ""
  ).trim();
  const retryKey = buildFeedbackEmailRetryKey(bookingId, roomId, interviewerEmail);
  if ((!bookingId && !roomId) || !interviewerEmail || !retryKey) return;
  const nextEntry = {
    retry_key: retryKey,
    booking_id: bookingId,
    room_id: roomId,
    interviewer_email: normalizeEmail(interviewerEmail),
    actor_email: normalizeEmail(
      payload?.actorEmail || payload?.actor_email || interviewerEmail
    ),
    updated_at: new Date().toISOString(),
  };
  const entries = readFeedbackEmailRetryEntries().filter(
    (entry) => String(entry?.retry_key || "").trim() !== retryKey
  );
  entries.push(nextEntry);
  writeFeedbackEmailRetryEntries(entries);
};

const removeFeedbackEmailRetryEntry = (bookingId, roomId, interviewerEmail) => {
  const retryKey = buildFeedbackEmailRetryKey(bookingId, roomId, interviewerEmail);
  if (!retryKey) return;
  const entries = readFeedbackEmailRetryEntries().filter(
    (entry) => String(entry?.retry_key || "").trim() !== retryKey
  );
  writeFeedbackEmailRetryEntries(entries);
};

const getStoredLiveInterviewUserEmails = () => {
  if (typeof window === "undefined") return [];
  const seen = new Set();
  const emails = [];
  for (const key of LIVE_INTERVIEW_USER_EMAIL_KEYS) {
    for (const value of [window.sessionStorage.getItem(key), window.localStorage.getItem(key)]) {
      const cleanValue = String(value || "").trim();
      const normalizedValue = normalizeEmail(cleanValue);
      if (!cleanValue || seen.has(normalizedValue)) continue;
      seen.add(normalizedValue);
      emails.push(cleanValue);
    }
  }
  return emails;
};

const getStoredLiveInterviewUserValue = (fallback = "", room = null, overrideEmail = "") => {
  const safeOverride = String(overrideEmail || "").trim();
  if (safeOverride) return safeOverride;
  const emails = getStoredLiveInterviewUserEmails();
  if (!emails.length) return fallback;
  const roomEmails = [room?.host_user_id, room?.created_by_email]
    .map((value) => normalizeEmail(value))
    .filter(Boolean);
  const matchedEmail = emails.find((email) => roomEmails.includes(normalizeEmail(email)));
  return matchedEmail || emails[0] || fallback;
};

const getCurrentUserEmail = (room = null, overrideEmail = "") =>
  getStoredLiveInterviewUserValue("", room, overrideEmail);

const getCleanJoinRequestError = (err) => {
  if (err?.status === 403) {
    return (
      err?.message ||
      "You do not have permission to admit candidates. Join the room with the interviewer or scheduler account, or sign in as that user."
    );
  }
  if (err?.status >= 500 || err?.responseType === "html" || err?.payload?.response_type === "html") {
    return "Unable to load join requests. Please try again.";
  }
  return err?.message || "Unable to load join requests. Please try again.";
};

const getPendingRequests = (requests = []) =>
  (requests || []).filter((request) => (request?.status || "pending") === "pending");

const getViewModeLabel = (role, isHost, isCoHost = false) => {
  if (role === "candidate") return "Candidate view";
  if (isHost) return "Host view";
  if (isCoHost) return "Co-host view";
  if (role === "interviewer") return "Interviewer view";
  return "Interviewer view";
};

const getLivekitStateLabel = (livekitReady, livekitConfigured) => {
  if (livekitReady) return "Video ready";
  if (livekitConfigured) return "Server check needed";
  return "Setup pending";
};

const normalizeRecordingStatus = (raw) => {
  const value = String(raw || "").trim().toLowerCase();
  if (["recording", "active", "started", "in_progress", "live", "on"].includes(value)) {
    return "recording";
  }
  if (["processing", "stopping", "uploading", "finalizing", "stopped"].includes(value)) {
    return "processing";
  }
  return "idle";
};

const normalizeJoinRequestStatus = (raw) => {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "pending";
  if (["approved", "accepted", "admitted", "allowed"].includes(s)) return "approved";
  if (["rejected", "reject", "declined", "decline", "denied", "deny"].includes(s)) return "rejected";
  if (["removed", "remove"].includes(s)) return "removed";
  if (s === "joined") return "joined";
  if (s === "pending") return "pending";
  if (s === "left") return "left";
  return s;
};

const getCandidateJoinStatusMessage = (status) => {
  const normalizedStatus = normalizeJoinRequestStatus(status);
  if (normalizedStatus === "approved") {
    return "You have been approved. The meeting will open automatically.";
  }
  if (normalizedStatus === "rejected") {
    return "Your join request was rejected. You can request to join again.";
  }
  if (normalizedStatus === "removed") {
    return REMOVED_FROM_INTERVIEW_MESSAGE;
  }
  if (normalizedStatus === "pending") {
    return "Waiting for the interviewer to admit you.";
  }
  if (normalizedStatus === "joined") {
    return "You are entering the meeting.";
  }
  if (normalizedStatus === "left") {
    return "You left the meeting. Click Request to Join when you want to enter again.";
  }
  return "";
};

const mergeCandidateOption = (items, seenKeys, name, email, source) => {
  const cleanName = String(name || "").trim();
  const cleanEmail = normalizeEmail(email);
  const uniqueKey = cleanEmail || cleanName.toLowerCase();
  if (!uniqueKey || seenKeys.has(uniqueKey)) {
    return;
  }
  seenKeys.add(uniqueKey);
  items.push({
    name: cleanName || "Candidate",
    email: cleanEmail,
    source: source || "Existing candidate",
  });
};

const copyTextValue = async (value) => {
  await navigator.clipboard.writeText(String(value || ""));
};

const pickLatestInviteUrlFromPayload = (payload) => {
  const direct = payload?.candidate_invite_url || payload?.candidate_invite?.invite_url;
  if (direct) return String(direct);
  const list = payload?.candidate_invites || [];
  if (!list.length) return "";
  const sorted = [...list].sort((a, b) => {
    const ta = a?.created_at ? Date.parse(a.created_at) : 0;
    const tb = b?.created_at ? Date.parse(b.created_at) : 0;
    return tb - ta;
  });
  return String(sorted[0]?.invite_url || "");
};

const LocalParticipantFallback = () => {
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false }
  );
  const localCameraTrack = cameraTracks.find((trackRef) => trackRef.participant?.isLocal);
  const remoteCameraCount = cameraTracks.filter((trackRef) => !trackRef.participant?.isLocal).length;

  if (!localCameraTrack || remoteCameraCount > 0) {
    return null;
  }

  return (
    <div className="live-interview-local-fallback">
      <LiveInterviewParticipantTile trackRef={localCameraTrack} />
    </div>
  );
};

const getChatSpeakerLabel = (message) => {
  const participant = message?.from;
  if (!participant) return "Someone";
  if (participant.isLocal) return "You";

  const displayName = String(participant.name || "").trim();
  if (displayName && displayName.toLowerCase() !== "participant") {
    return displayName;
  }

  const identity = (participant.identity || "").toLowerCase();
  if (identity.startsWith("candidate-")) return "Candidate";
  if (identity.startsWith("host-") || identity.startsWith("interviewer-")) return "Interviewer";
  return "Participant";
};

const CHAT_ATTACHMENT_MESSAGE_TYPE = "searchjob_meet_chat_attachment";
const CHAT_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
const CHAT_ATTACHMENT_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.webp";

const parseChatAttachmentMessage = (message) => {
  const serialized = String(message || "").trim();
  if (!serialized.startsWith("{")) return null;
  try {
    const payload = JSON.parse(serialized);
    const attachment = payload?.attachment;
    const id = String(attachment?.id || "").trim();
    const name = String(attachment?.name || "").trim();
    if (
      payload?.type !== CHAT_ATTACHMENT_MESSAGE_TYPE ||
      payload?.version !== 1 ||
      !/^[a-f0-9]{32}$/i.test(id) ||
      !name
    ) {
      return null;
    }
    const size = Number(attachment?.size || 0);
    return {
      id,
      name,
      size: Number.isFinite(size) && size >= 0 ? size : 0,
      mimeType: String(attachment?.mime_type || "").trim(),
      caption: String(payload?.caption || "").trim(),
    };
  } catch {
    return null;
  }
};

const createChatAttachmentMessage = (attachment, caption = "") =>
  JSON.stringify({
    type: CHAT_ATTACHMENT_MESSAGE_TYPE,
    version: 1,
    attachment: {
      id: attachment.id,
      name: attachment.name,
      size: attachment.size,
      mime_type: attachment.mime_type || "",
    },
    caption: String(caption || "").trim().slice(0, 2000),
  });

const formatChatAttachmentSize = (size) => {
  const bytes = Number(size || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "File";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
};

const isChatImageAttachment = (attachment) => {
  const mimeType = String(
    attachment?.mimeType || attachment?.mime_type || attachment?.type || ""
  ).toLowerCase();
  if (["image/png", "image/jpeg", "image/gif", "image/webp"].includes(mimeType)) {
    return true;
  }
  return /\.(png|jpe?g|gif|webp)$/i.test(String(attachment?.name || ""));
};

const isPersistedChatRowFromSelf = (row, localIdentity) => {
  const senderIdentity = String(row?.sender_identity || "").trim();
  const lid = String(localIdentity || "").trim();
  return Boolean(lid && senderIdentity && senderIdentity === lid);
};

const formatChatTime = (timestamp) => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const getPersistedChatRowKey = (row) =>
  [
    row?.message_id || "",
    row?.timestamp || "",
    row?.sender_identity || "",
    row?.message || "",
  ].join("::");

const getChatMessageKey = (message, fallbackIndex = 0) =>
  [
    message?.id || "",
    message?.timestamp instanceof Date ? message.timestamp.toISOString() : String(message?.timestamp || ""),
    message?.from?.identity || "",
    message?.message || "",
    fallbackIndex,
  ].join("::");

const getChatNotificationPreview = (message) => {
  const attachment = parseChatAttachmentMessage(message?.message);
  if (attachment) {
    return attachment.caption
      ? `shared ${attachment.name}: ${attachment.caption}`
      : `shared ${attachment.name}`;
  }
  const cleanText = String(message?.message || "").trim();
  if (!cleanText) return "sent a message";
  if (cleanText.length <= 96) return cleanText;
  return `${cleanText.slice(0, 93)}...`;
};

const requestBrowserNotificationPermission = async () => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  if (window.Notification.permission !== "default") {
    return window.Notification.permission;
  }
  try {
    return await window.Notification.requestPermission();
  } catch (error) {
    console.warn("[LiveInterview] Browser notification permission request failed", error);
    return window.Notification.permission || "default";
  }
};

/** LiveKit sometimes omits or mis-sets from.isLocal; compare identities as a fallback. */
const isChatMessageFromSelf = (message, localIdentity) => {
  if (!message) return false;
  if (message.from?.isLocal) return true;
  const fid = String(message.from?.identity || "").trim();
  const lid = String(localIdentity || "").trim();
  return Boolean(lid && fid && fid === lid);
};

const toPersistedChatEntry = (row) => ({
  id: row?.message_id,
  message: row?.message || "",
  timestamp: row?.timestamp ? new Date(row.timestamp) : new Date(),
  from: {
    identity: row?.sender_identity || "",
    name: row?.sender_name || row?.sender_role || "Participant",
    isLocal: false,
  },
});

const ChatImageAttachmentPreview = ({
  attachment,
  roomId,
  actorEmail,
  actorIdentity,
  actorRole,
  inviteToken,
  onOpen,
  onDownload,
}) => {
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState("");
  const attachmentId = String(attachment?.id || "").trim();
  const attachmentName = String(attachment?.name || "").trim();

  useEffect(() => {
    let cancelled = false;
    setPreviewUrl("");
    setPreviewError("");
    if (!roomId || !attachmentId || !attachmentName) {
      setPreviewError("Image preview is unavailable.");
      return undefined;
    }

    getLiveInterviewChatAttachmentDownload(roomId, attachmentId, {
      actorEmail,
      actorIdentity,
      role: actorRole || "interviewer",
      inviteToken,
      fileName: attachmentName,
      inline: true,
    })
      .then((payload) => {
        const downloadUrl = String(payload?.download_url || "").trim();
        if (!downloadUrl) {
          throw new Error("Image preview is unavailable.");
        }
        if (!cancelled) {
          setPreviewUrl(downloadUrl);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewError("Image preview is unavailable.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [actorEmail, actorIdentity, actorRole, attachmentId, attachmentName, inviteToken, roomId]);

  if (previewError) {
    return (
      <div className="li-chat-image-unavailable" role="status">
        <ImageIcon size={18} aria-hidden />
        <span>Image preview unavailable</span>
        <button type="button" onClick={() => onDownload?.(attachment)}>
          Download
        </button>
      </div>
    );
  }

  if (!previewUrl) {
    return (
      <div className="li-chat-image-loading" role="status" aria-live="polite">
        <ImageIcon size={20} aria-hidden />
        <span>Loading image…</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="li-chat-image-preview"
      onClick={(event) =>
        onOpen?.({
          attachment,
          sourceUrl: previewUrl,
          trigger: event.currentTarget,
        })
      }
      aria-label={`Open image ${attachmentName}`}
      title={`Open ${attachmentName}`}
    >
      <img
        src={previewUrl}
        alt={attachment.caption || attachmentName}
        loading="lazy"
        onError={() => {
          setPreviewUrl("");
          setPreviewError("Image preview is unavailable.");
        }}
      />
    </button>
  );
};

const LiveInterviewChatPanel = ({
  chatMessages,
  send,
  isSending,
  roomId = "",
  actorEmail = "",
  actorIdentity = "",
  actorRole = "",
  inviteToken = "",
  displayName = "",
  localIdentity = "",
}) => {
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [historyMessages, setHistoryMessages] = useState([]);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [selectedAttachmentPreviewUrl, setSelectedAttachmentPreviewUrl] = useState("");
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState("");
  const [activeChatImage, setActiveChatImage] = useState(null);
  const listRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const persistedKeysRef = useRef(new Set());
  const chatImageCloseButtonRef = useRef(null);
  const activeChatImageTriggerRef = useRef(null);

  useEffect(() => {
    const safeRoomId = String(roomId || "").trim();
    if (!safeRoomId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { listLiveInterviewChatMessages } = await import("../api/liveInterviewApi");
        const payload = await listLiveInterviewChatMessages(safeRoomId, {
          actorEmail,
          actorIdentity,
          role: actorRole || "interviewer",
          inviteToken,
        });
        if (cancelled) return;
        const rows = payload?.messages || [];
        setHistoryMessages(rows.map(toPersistedChatEntry).filter((item) => item.message));
        rows.forEach((row, index) => {
          persistedKeysRef.current.add(`${row?.message_id || ""}::${row?.timestamp || ""}::${index}`);
        });
      } catch (err) {
        if (!cancelled) {
          console.warn("[LiveInterview] Failed to load persisted chat", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [actorEmail, actorIdentity, actorRole, inviteToken, roomId]);

  const mergedMessages = useMemo(() => {
    const merged = [...historyMessages];
    (chatMessages || []).forEach((entry, index) => {
      const key = getChatMessageKey(entry, index);
      const exists = merged.some((item, itemIndex) => getChatMessageKey(item, itemIndex) === key);
      if (!exists) {
        merged.push(entry);
      }
    });
    return merged.sort((a, b) => {
      const aTime = a?.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a?.timestamp || 0).getTime();
      const bTime = b?.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b?.timestamp || 0).getTime();
      return aTime - bTime;
    });
  }, [chatMessages, historyMessages]);

  const persistChatMessage = useCallback(
    async (entry, fallbackIndex = 0) => {
      const safeRoomId = String(roomId || "").trim();
      const cleanText = String(entry?.message || "").trim();
      if (!safeRoomId || !cleanText) return;
      const persistKey = getChatMessageKey(entry, fallbackIndex);
      if (persistedKeysRef.current.has(persistKey)) return;
      persistedKeysRef.current.add(persistKey);
      try {
        const { saveLiveInterviewChatMessage } = await import("../api/liveInterviewApi");
        await saveLiveInterviewChatMessage(safeRoomId, {
          message: cleanText,
          actor_email: actorEmail,
          actor_identity: actorIdentity || entry?.from?.identity || "",
          sender_identity: entry?.from?.identity || actorIdentity || "",
          sender_name: entry?.from?.name || displayName || "",
          sender_role: actorRole,
          role: actorRole,
          invite_token: inviteToken,
        });
      } catch (err) {
        persistedKeysRef.current.delete(persistKey);
        console.warn("[LiveInterview] Chat persistence failed", err);
      }
    },
    [actorEmail, actorIdentity, actorRole, displayName, inviteToken, roomId]
  );

  useEffect(() => {
    (chatMessages || []).forEach((entry, index) => {
      if (isChatMessageFromSelf(entry, localIdentity)) return;
      persistChatMessage(entry, index);
    });
  }, [chatMessages, localIdentity, persistChatMessage]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [mergedMessages.length]);

  const clearSelectedAttachment = () => {
    setSelectedAttachment(null);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (
      !selectedAttachment ||
      !isChatImageAttachment({
        name: selectedAttachment.name,
        mimeType: selectedAttachment.type,
      })
    ) {
      setSelectedAttachmentPreviewUrl("");
      return undefined;
    }
    const nextPreviewUrl = URL.createObjectURL(selectedAttachment);
    setSelectedAttachmentPreviewUrl(nextPreviewUrl);
    return () => URL.revokeObjectURL(nextPreviewUrl);
  }, [selectedAttachment]);

  const openChatImage = useCallback(({ attachment, sourceUrl, trigger }) => {
    activeChatImageTriggerRef.current = trigger || document.activeElement;
    setActiveChatImage({ attachment, sourceUrl });
  }, []);

  const closeChatImage = useCallback(() => {
    setActiveChatImage(null);
    const trigger = activeChatImageTriggerRef.current;
    activeChatImageTriggerRef.current = null;
    window.requestAnimationFrame(() => trigger?.focus?.());
  }, []);

  useEffect(() => {
    if (!activeChatImage) return undefined;
    const focusTimer = window.setTimeout(() => chatImageCloseButtonRef.current?.focus(), 0);
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeChatImage();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeChatImage, closeChatImage]);

  const handleAttachmentSelection = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    if (file.size > CHAT_ATTACHMENT_MAX_BYTES) {
      clearSelectedAttachment();
      setError("Files must be 25 MB or smaller.");
      return;
    }
    setError("");
    setSelectedAttachment(file);
  };

  const handleAttachmentDownload = async (attachment) => {
    if (!attachment?.id || downloadingAttachmentId) return;
    setDownloadingAttachmentId(attachment.id);
    setError("");
    try {
      const payload = await getLiveInterviewChatAttachmentDownload(roomId, attachment.id, {
        actorEmail,
        actorIdentity,
        role: actorRole || "interviewer",
        inviteToken,
        fileName: attachment.name,
      });
      const downloadUrl = String(payload?.download_url || "").trim();
      if (!downloadUrl) {
        throw new Error("The file is not available for download.");
      }
      if (payload?.temporary) {
        const response = await fetch(downloadUrl, { credentials: "include" });
        if (!response.ok) {
          const failure = await response.json().catch(() => null);
          throw new Error(failure?.error || "The temporary file is no longer available.");
        }
        const objectUrl = URL.createObjectURL(await response.blob());
        const downloadLink = document.createElement("a");
        downloadLink.href = objectUrl;
        downloadLink.download = attachment.name || "meeting-file";
        downloadLink.style.display = "none";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
        return;
      }
      window.location.assign(downloadUrl);
    } catch (err) {
      console.error("[LiveInterview] Failed to download chat attachment", err);
      setError(err.message || "File could not be downloaded.");
    } finally {
      setDownloadingAttachmentId("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const cleanMessage = message.trim();
    const fileToShare = selectedAttachment;
    if (!cleanMessage && !fileToShare) return;

    setError("");
    if (fileToShare) setUploadingAttachment(true);
    try {
      let outgoingMessage = cleanMessage;
      if (fileToShare) {
        const uploadPayload = await uploadLiveInterviewChatAttachment(roomId, fileToShare, {
          actorEmail,
          actorIdentity,
          role: actorRole || "interviewer",
          inviteToken,
        });
        const attachment = uploadPayload?.attachment;
        if (!attachment?.id || !attachment?.name) {
          throw new Error("The uploaded file could not be prepared for chat.");
        }
        outgoingMessage = createChatAttachmentMessage(attachment, cleanMessage);
      }

      await send(outgoingMessage);
      await persistChatMessage(
        {
          message: outgoingMessage,
          timestamp: new Date(),
          from: {
            identity: actorIdentity || localIdentity,
            name: displayName || "You",
            isLocal: true,
          },
        },
        Date.now()
      );
      setMessage("");
      if (fileToShare) clearSelectedAttachment();
    } catch (err) {
      console.error("[LiveInterview] Failed to send chat content", err);
      setError(err.message || (fileToShare ? "File could not be shared." : "Message could not be sent."));
    } finally {
      if (fileToShare) setUploadingAttachment(false);
    }
  };

  return (
    <div className="li-chat-panel">
      <div className="li-chat-list" ref={listRef}>
        {mergedMessages.length === 0 ? (
          <div className="li-chat-empty">
            <div className="li-chat-empty-icon" aria-hidden>
              <MessageSquare size={26} />
            </div>
            <strong>No messages yet</strong>
            <span>
              Share context and files with people in this meeting.
            </span>
          </div>
        ) : (
          mergedMessages.map((entry, index) => {
            const isSelf = isChatMessageFromSelf(entry, localIdentity);
            const speakerLabel = getChatSpeakerLabel(entry);
            const initial = String(speakerLabel || "?").charAt(0).toUpperCase();
            const attachment = parseChatAttachmentMessage(entry.message);
            return (
              <div
                className={`li-chat-row ${isSelf ? "is-self" : "is-other"}`}
                key={`${entry.timestamp}-${index}`}
              >
                {!isSelf ? (
                  <div className="li-chat-avatar" aria-hidden>
                    {initial}
                  </div>
                ) : null}
                <div className="li-chat-bubble-wrap">
                  <div className="li-chat-meta">
                    <strong>{isSelf ? "You" : speakerLabel}</strong>
                    <span>{formatChatTime(entry.timestamp)}</span>
                  </div>
                  <div
                    className={`li-chat-bubble ${isSelf ? "is-self" : "is-other"} ${
                      attachment ? "has-attachment" : ""
                    }`}
                  >
                    {attachment ? (
                      <>
                        {attachment.caption ? (
                          <p className="li-chat-attachment-caption">{attachment.caption}</p>
                        ) : null}
                        {isChatImageAttachment(attachment) ? (
                          <ChatImageAttachmentPreview
                            attachment={attachment}
                            roomId={roomId}
                            actorEmail={actorEmail}
                            actorIdentity={actorIdentity}
                            actorRole={actorRole}
                            inviteToken={inviteToken}
                            onOpen={openChatImage}
                            onDownload={handleAttachmentDownload}
                          />
                        ) : (
                          <div className="li-chat-attachment">
                            <FileText size={19} aria-hidden />
                            <div className="li-chat-attachment-copy">
                              <strong title={attachment.name}>{attachment.name}</strong>
                              <span>{formatChatAttachmentSize(attachment.size)}</span>
                            </div>
                            <button
                              type="button"
                              className="li-chat-attachment-download"
                              onClick={() => handleAttachmentDownload(attachment)}
                              disabled={Boolean(downloadingAttachmentId)}
                              aria-label={`Download ${attachment.name}`}
                            >
                              <Download size={15} aria-hidden />
                              {downloadingAttachmentId === attachment.id ? "Getting link…" : "Download"}
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      entry.message
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {error ? <div className="li-chat-error">{error}</div> : null}

      <form className="li-chat-composer" onSubmit={handleSubmit}>
        <input
          ref={attachmentInputRef}
          type="file"
          className="li-chat-attachment-input"
          accept={CHAT_ATTACHMENT_ACCEPT}
          onChange={handleAttachmentSelection}
        />
        {selectedAttachment ? (
          <div className="li-chat-selected-attachment">
            {selectedAttachmentPreviewUrl ? (
              <img
                className="li-chat-selected-image"
                src={selectedAttachmentPreviewUrl}
                alt="Selected attachment preview"
              />
            ) : (
              <FileText size={16} aria-hidden />
            )}
            <span title={selectedAttachment.name}>{selectedAttachment.name}</span>
            <small>{formatChatAttachmentSize(selectedAttachment.size)}</small>
            <button
              type="button"
              onClick={clearSelectedAttachment}
              disabled={uploadingAttachment}
              aria-label={`Remove ${selectedAttachment.name}`}
              title="Remove attachment"
            >
              <X size={14} aria-hidden />
            </button>
          </div>
        ) : null}
        <div className="li-chat-composer-row">
          <input
            className="li-chat-input"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={selectedAttachment ? "Add a message (optional)" : "Send a message to everyone"}
            disabled={uploadingAttachment}
          />
          <button
            type="button"
            className="li-chat-attach"
            onClick={() => attachmentInputRef.current?.click()}
            disabled={uploadingAttachment}
            aria-label="Attach a file"
            title="Attach a file"
          >
            <Paperclip size={17} aria-hidden />
          </button>
          <button
            className="li-chat-send"
            type="submit"
            disabled={(!message.trim() && !selectedAttachment) || isSending || uploadingAttachment}
          >
            <Send size={16} />
            {uploadingAttachment ? "Sharing…" : "Send"}
          </button>
        </div>
      </form>
      {activeChatImage ? (
        <div
          className="li-chat-image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`Image preview: ${activeChatImage.attachment.name}`}
          onClick={(event) => {
            if (event.target === event.currentTarget) closeChatImage();
          }}
          onKeyDown={(event) => {
            if (event.key === "Tab") {
              event.preventDefault();
              chatImageCloseButtonRef.current?.focus();
            }
          }}
        >
          <div className="li-chat-image-lightbox-content">
            <button
              ref={chatImageCloseButtonRef}
              type="button"
              className="li-chat-image-lightbox-close"
              onClick={closeChatImage}
              aria-label="Close image preview"
              title="Close"
            >
              <X size={20} aria-hidden />
            </button>
            <img
              src={activeChatImage.sourceUrl}
              alt={activeChatImage.attachment.caption || activeChatImage.attachment.name}
            />
            <div className="li-chat-image-lightbox-footer">
              <span title={activeChatImage.attachment.name}>
                {activeChatImage.attachment.name}
              </span>
              <button
                type="button"
                onClick={() => handleAttachmentDownload(activeChatImage.attachment)}
                disabled={Boolean(downloadingAttachmentId)}
              >
                <Download size={16} aria-hidden />
                {downloadingAttachmentId === activeChatImage.attachment.id
                  ? "Getting link…"
                  : "Download"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const parseParticipantMetadata = (participant) => {
  try {
    return JSON.parse(participant?.metadata || "{}");
  } catch {
    return {};
  }
};

const getParticipantRole = (participant) => {
  const metadata = parseParticipantMetadata(participant);
  const identity = (participant?.identity || "").toLowerCase();
  const role = String(metadata.role || "").toLowerCase();

  if (metadata.is_host || role === "host" || identity.startsWith("host-")) return "host";
  if (role === "candidate" || identity.startsWith("candidate-")) return "candidate";
  if (role === "interviewer" || identity.startsWith("interviewer-")) return "interviewer";
  return "participant";
};

const getParticipantLabel = (participant, roleCounts, resolveDisplayName) => {
  if (participant?.isLocal) return "You";

  const role = getParticipantRole(participant);
  roleCounts[role] = (roleCounts[role] || 0) + 1;

  const resolvedName = String(resolveDisplayName?.(participant) || "").trim();
  if (
    resolvedName &&
    !isGenericParticipantLabel(resolvedName) &&
    !isEmailLike(resolvedName)
  ) {
    return roleCounts[role] > 1 ? `${resolvedName} (${roleCounts[role]})` : resolvedName;
  }

  if (role === "host") return roleCounts[role] > 1 ? `Host ${roleCounts[role]}` : "Host";
  if (role === "candidate") return roleCounts[role] > 1 ? `Candidate ${roleCounts[role]}` : "Candidate";
  if (role === "interviewer") return roleCounts[role] > 1 ? `Interviewer ${roleCounts[role]}` : "Interviewer";
  return roleCounts[role] > 1 ? `Participant ${roleCounts[role]}` : "Participant";
};

const buildParticipantNameResolver = (candidateOptions = [], { instantOpenJoin = false } = {}) => {
  const byEmail = new Map();
  let primaryCandidateName = "";

  candidateOptions.forEach((option) => {
    const name = String(option?.name || "").trim();
    const email = normalizeEmail(option?.email || "");
    if (!name || isGenericParticipantLabel(name)) return;
    if (option?.source === "Primary candidate" || !primaryCandidateName) {
      primaryCandidateName = name;
    }
    if (email) byEmail.set(email, name);
  });

  return (participant) => {
    const metadata = parseParticipantMetadata(participant);
    const metaName = String(
      metadata.display_name || metadata.displayName || metadata.name || ""
    ).trim();
    if (metaName && !isGenericParticipantLabel(metaName) && !isEmailLike(metaName)) {
      return metaName;
    }

    const livekitName = resolveParticipantDisplayName(participant);
    if (
      livekitName &&
      !isGenericParticipantLabel(livekitName) &&
      !isEmailLike(livekitName) &&
      !["Candidate", "Interviewer", "Host", "Participant"].includes(livekitName)
    ) {
      return livekitName;
    }

    const email = normalizeEmail(metadata.email || metadata.candidate_email || "");
    if (email && byEmail.has(email)) return byEmail.get(email);

    if (!instantOpenJoin && getParticipantRole(participant) === "candidate" && primaryCandidateName) {
      return primaryCandidateName;
    }

    return "";
  };
};

const getParticipantStatus = (participant) => {
  if (participant?.isSpeaking) return "Speaking";
  return participant?.isLocal ? "You are in the call" : "In the call";
};

const ParticipantRosterPanel = ({
  canManageParticipants,
  canManageCandidateInvites,
  canMuteCandidates,
  instantOpenJoin = false,
  roomId,
  actorEmail,
  actorIdentity,
  role,
  operationalHostEmail = "",
  candidateOptions,
  joinRequestState,
  onRoomDataUpdate,
  onRemoveParticipant,
  onMuteAllCandidates,
  onMuteCandidate,
  onMuteSelectedIdentities,
  removeBusyIdentity,
  removeError,
  muteAllBusy,
  muteCandidateBusyIdentity,
  muteControlsError,
  onHostActionToast,
  onMuteControlErrorChange,
}) => {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();
  const resolveParticipantDisplayNameForRoster = useMemo(
    () => buildParticipantNameResolver(candidateOptions, { instantOpenJoin }),
    [candidateOptions, instantOpenJoin]
  );
  const [bulkMuteSelection, setBulkMuteSelection] = useState([]);
  const [unmuteRequestBusyKey, setUnmuteRequestBusyKey] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteOverlayOpen, setInviteOverlayOpen] = useState(false);
  const [inviteOverlayPhase, setInviteOverlayPhase] = useState("idle");
  const [overlayInviteUrl, setOverlayInviteUrl] = useState("");
  const [inviteCopySuccess, setInviteCopySuccess] = useState(false);
  // `role` is the existing viewer role supplied by RoomPage. Candidate-facing
  // rosters should not expose interview-team role labels, while staff retain
  // the current Host, Co-host, and Interviewer badges below.
  const showParticipantRoleBadges = role !== "candidate";
  const normalizedOperationalHost = normalizeEmail(operationalHostEmail);
  const getStaffParticipantEmail = (participant) => {
    const metadata = parseParticipantMetadata(participant);
    return normalizeEmail(metadata.email || "");
  };
  const roleCounts = {};
  const rows = participants.map((participant) => {
    const participantRole = getParticipantRole(participant);
    const label = getParticipantLabel(participant, roleCounts, resolveParticipantDisplayNameForRoster);
    const participantEmail = getStaffParticipantEmail(participant);
    const metadata = parseParticipantMetadata(participant);
    const isOperationalHost =
      Boolean(normalizedOperationalHost) && participantEmail === normalizedOperationalHost;
    const isCoHost = !isOperationalHost && Boolean(metadata.is_cohost);
    return {
      participant,
      role: participantRole,
      label,
      isOperationalHost,
      isCoHost,
      initials: label === "You" ? "Y" : getParticipantInitials(label).slice(0, 2),
    };
  });
  const normalizedSearch = searchValue.trim().toLowerCase();
  const filteredRows = rows.filter(({ participant, role, label }) =>
    !normalizedSearch || [
      label,
      role,
      participant.identity,
      participant.name,
      participant.metadata,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedSearch))
  );
  const remoteCandidateIdentities = rows
    .filter(({ participant, role }) => role === "candidate" && !participant.isLocal)
    .map(({ participant }) => participant.identity);
  const remoteMutedCandidateIdentities = rows
    .filter(
      ({ participant, role }) =>
        role === "candidate" && !participant.isLocal && !participant.isMicrophoneEnabled
    )
    .map(({ participant }) => participant.identity);
  const remoteCandidateIdentityKey = remoteCandidateIdentities.join("|");
  const remoteMutedCandidateIdentityKey = remoteMutedCandidateIdentities.join("|");
  const remoteCandidateIdentitySet = useMemo(
    () => new Set(remoteCandidateIdentities),
    [remoteCandidateIdentityKey]
  );
  const remoteMutedCandidateIdentitySet = useMemo(
    () => new Set(remoteMutedCandidateIdentities),
    [remoteMutedCandidateIdentityKey]
  );
  const toggleBulkMuteSelect = (identity) => {
    setBulkMuteSelection((prev) =>
      prev.includes(identity) ? prev.filter((id) => id !== identity) : [...prev, identity]
    );
  };
  const selectAllRemoteCandidatesForMute = () => {
    setBulkMuteSelection(remoteCandidateIdentities);
  };
  useEffect(() => {
    setBulkMuteSelection((prev) => {
      const next = prev.filter((identity) => remoteCandidateIdentitySet.has(identity));
      return next.length === prev.length && next.every((identity, index) => identity === prev[index])
        ? prev
        : next;
    });
  }, [remoteCandidateIdentitySet]);
  const selectedMutedCandidateIdentities = bulkMuteSelection.filter((identity) =>
    remoteMutedCandidateIdentitySet.has(identity)
  );
  const publishCandidateUnmuteRequest = useCallback(
    async (targetIdentities, successMessage, busyKey) => {
      onMuteControlErrorChange?.("");
      setUnmuteRequestBusyKey(busyKey);
      try {
        const actorName =
          resolveParticipantDisplayName(localParticipant) ||
          String(localParticipant?.name || "").trim() ||
          "Interviewer";
        const requestedIdentities = await publishUnmuteRequest(room, localParticipant, {
          senderName: actorName,
          targetIdentities,
        });
        onHostActionToast?.(successMessage);
        return requestedIdentities;
      } catch (error) {
        onMuteControlErrorChange?.(
          error?.message || "Could not send the unmute request."
        );
        return null;
      } finally {
        setUnmuteRequestBusyKey("");
      }
    },
    [localParticipant, onHostActionToast, onMuteControlErrorChange, room]
  );
  const handleCandidateAudioAction = useCallback(
    async (identity, micOn, label) => {
      if (!identity) return;
      if (micOn) {
        onMuteCandidate?.(identity, true);
        return;
      }
      await publishCandidateUnmuteRequest(
        [identity],
        `Unmute request sent to ${label || "the candidate"}.`,
        `single:${identity}`
      );
    },
    [onMuteCandidate, publishCandidateUnmuteRequest]
  );
  const runMuteSelectedCandidates = async (wantMuted) => {
    const ids = bulkMuteSelection.filter((id) => remoteCandidateIdentitySet.has(id));
    if (!ids.length) return;
    if (!wantMuted) {
      if (!selectedMutedCandidateIdentities.length) {
        onHostActionToast?.("No muted selected candidates need an unmute request.");
        return;
      }
      const requested = await publishCandidateUnmuteRequest(
        selectedMutedCandidateIdentities,
        `Unmute request sent to ${selectedMutedCandidateIdentities.length} selected candidate(s).`,
        "selected"
      );
      if (requested?.length) {
        setBulkMuteSelection([]);
      }
      return;
    }
    if (!onMuteSelectedIdentities) return;
    const ok = await onMuteSelectedIdentities(ids, true);
    if (ok) setBulkMuteSelection([]);
  };
  const requestAllCandidatesToUnmute = useCallback(async () => {
    if (!remoteMutedCandidateIdentities.length) {
      onHostActionToast?.("No muted candidates need an unmute request.");
      return;
    }
    await publishCandidateUnmuteRequest(
      remoteMutedCandidateIdentities,
      `Unmute request sent to ${remoteMutedCandidateIdentities.length} candidate(s).`,
      "all"
    );
  }, [
    onHostActionToast,
    publishCandidateUnmuteRequest,
    remoteMutedCandidateIdentities,
  ]);
  const pendingRequests = getPendingRequests(joinRequestState?.requests || []);
  const filteredPendingRequests = pendingRequests.filter((request) =>
    !normalizedSearch || [
      request.display_name,
      request.email,
      request.role,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedSearch))
  );

  const openQuickCreateInvite = async () => {
    if (!canManageCandidateInvites || !roomId) return;
    setInviteOverlayOpen(true);
    setInviteOverlayPhase("creating");
    setOverlayInviteUrl("");
    setInviteError("");
    setInviteCopySuccess(false);
    try {
      const payload = await createLiveInterviewCandidateInvite(roomId, {
        actor_email: actorEmail,
        actor_identity: actorIdentity,
        role,
        candidate_name: "Candidate",
        send_email: false,
        // instantOpenJoin is passed from parent (instant booking / open_join room).
        candidate_join_mode: instantOpenJoin ? "direct" : undefined,
      });
      const inviteUrl = pickLatestInviteUrlFromPayload(payload);
      if (!inviteUrl) {
        throw new Error("Invite link was created but no URL was returned.");
      }
      onRoomDataUpdate?.(payload);
      setOverlayInviteUrl(inviteUrl);
      setInviteOverlayPhase("success");
    } catch (err) {
      setInviteError(err.message || "Candidate invite could not be created.");
      setInviteOverlayPhase("error");
    }
  };

  const closeInviteOverlay = () => {
    setInviteOverlayOpen(false);
    setInviteOverlayPhase("idle");
    setOverlayInviteUrl("");
    setInviteError("");
    setInviteCopySuccess(false);
  };

  const handleOverlayCopy = async () => {
    if (!overlayInviteUrl) return;
    try {
      await copyTextValue(overlayInviteUrl);
      setInviteCopySuccess(true);
      setInviteError("");
    } catch {
      setInviteCopySuccess(false);
      setInviteError("Copy failed. Select the link and copy manually.");
    }
  };

  return (
    <div className="live-interview-roster-panel">
      {inviteOverlayOpen ? (
        <div
          className="live-interview-invite-mini-overlay"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget && inviteOverlayPhase !== "creating") {
              closeInviteOverlay();
            }
          }}
        >
          <div
            className="live-interview-invite-mini-card"
            role="dialog"
            aria-labelledby="live-interview-invite-created-title"
            onClick={(event) => event.stopPropagation()}
          >
            {inviteOverlayPhase === "creating" ? (
              <>
                <h3 id="live-interview-invite-created-title">Creating link…</h3>
                <p className="live-interview-subtitle" style={{ margin: 0 }}>
                  Generating a candidate join link for this room.
                </p>
                <div className="live-interview-invite-mini-actions">
                  <button className="live-interview-btn" type="button" onClick={closeInviteOverlay}>
                    Cancel
                  </button>
                </div>
              </>
            ) : null}
            {inviteOverlayPhase === "success" && overlayInviteUrl ? (
              <>
                <h3 id="live-interview-invite-created-title">Candidate link created</h3>
                <div className="live-interview-link-row">
                  <input
                    className="li-invite-link-field"
                    value={overlayInviteUrl}
                    readOnly
                    onFocus={(event) => event.target.select()}
                    aria-label="Candidate join link"
                  />
                </div>
                {inviteCopySuccess ? (
                  <p className="live-interview-invite-copy-success" role="status" aria-live="polite">
                    Link copied to clipboard.
                  </p>
                ) : null}
                {inviteError ? <div className="li-invite-error">{inviteError}</div> : null}
                <div className="live-interview-invite-mini-actions">
                  <button
                    className={`li-invite-copy-btn ${inviteCopySuccess ? "is-success" : ""}`}
                    type="button"
                    onClick={handleOverlayCopy}
                  >
                    <Copy size={16} aria-hidden />
                    {inviteCopySuccess ? "Copied!" : "Copy link"}
                  </button>
                  <button className="li-invite-close-btn" type="button" onClick={closeInviteOverlay}>
                    Close
                  </button>
                </div>
              </>
            ) : null}
            {inviteOverlayPhase === "error" ? (
              <>
                <h3 id="live-interview-invite-created-title">Could not create link</h3>
                {inviteError ? <div className="live-interview-error">{inviteError}</div> : null}
                <div className="live-interview-invite-mini-actions">
                  <button className="live-interview-btn primary" type="button" onClick={closeInviteOverlay}>
                    Close
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Top toolbar — search + single overflow menu for host bulk actions.
          Previous design exposed every action (Mute all, bulk select, copy
          link, create invite) as a separate row of buttons, which made the
          panel look cluttered and ghosted. */}
      <div className="li-people-toolbar">
        <div className="li-people-search">
          <Search size={15} aria-hidden />
          <input
            className="li-people-search-input"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search people"
          />
        </div>
        {(canManageCandidateInvites || canMuteCandidates) ? (
          <details className="li-people-overflow">
            <summary
              className="li-people-overflow-trigger"
              aria-label="Participant management actions"
            >
              <MoreVertical size={16} aria-hidden />
            </summary>
            <div className="li-people-overflow-menu" role="menu">
              {canManageCandidateInvites ? (
                <button
                  type="button"
                  role="menuitem"
                  className="li-people-overflow-item"
                  onClick={(event) => {
                    event.currentTarget.closest("details")?.removeAttribute("open");
                    openQuickCreateInvite();
                  }}
                >
                  <UserPlus size={14} aria-hidden />
                  <span>Create participant link</span>
                </button>
              ) : null}
              {canMuteCandidates ? (
                <button
                  type="button"
                  role="menuitem"
                  className="li-people-overflow-item is-danger"
                  disabled={muteAllBusy}
                  onClick={(event) => {
                    event.currentTarget.closest("details")?.removeAttribute("open");
                    onMuteAllCandidates?.(true);
                  }}
                >
                  <MicOff size={14} aria-hidden />
                  <span>Mute all candidates</span>
                </button>
              ) : null}
              {canMuteCandidates ? (
                <button
                  type="button"
                  role="menuitem"
                  className="li-people-overflow-item"
                  disabled={!remoteMutedCandidateIdentities.length || Boolean(unmuteRequestBusyKey)}
                  onClick={(event) => {
                    event.currentTarget.closest("details")?.removeAttribute("open");
                    void requestAllCandidatesToUnmute();
                  }}
                >
                  <Mic size={14} aria-hidden />
                  <span>Request all to unmute</span>
                </button>
              ) : null}
            </div>
          </details>
        ) : null}
      </div>

      {muteControlsError ? (
        <div className="li-people-error">{muteControlsError}</div>
      ) : null}

      {canMuteCandidates && remoteCandidateIdentities.length > 0 ? (
        <div className="live-interview-roster-mute-bar">
          <span className="live-interview-roster-mute-label">
            Select candidates below to mute them or send an unmute request.
          </span>
          <div className="live-interview-roster-mute-actions">
            <button
              type="button"
              className="live-interview-btn"
              onClick={selectAllRemoteCandidatesForMute}
              disabled={muteAllBusy || Boolean(unmuteRequestBusyKey)}
            >
              Select all
            </button>
            {bulkMuteSelection.length ? (
              <button
                type="button"
                className="live-interview-btn"
                onClick={() => setBulkMuteSelection([])}
                disabled={muteAllBusy || Boolean(unmuteRequestBusyKey)}
              >
                Clear selection
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {canMuteCandidates && bulkMuteSelection.length > 0 ? (
        <div className="live-interview-roster-mute-select-bar">
          <span className="live-interview-roster-mute-label">
            Manage the selected candidates.
          </span>
          <div className="live-interview-roster-mute-actions">
            <button
              type="button"
              className="live-interview-btn warning"
              onClick={() => {
                void runMuteSelectedCandidates(true);
              }}
              disabled={muteAllBusy || Boolean(unmuteRequestBusyKey)}
            >
              Mute selected
            </button>
            <button
              type="button"
              className="live-interview-btn primary"
              onClick={() => {
                void runMuteSelectedCandidates(false);
              }}
              disabled={
                muteAllBusy ||
                Boolean(unmuteRequestBusyKey) ||
                !selectedMutedCandidateIdentities.length
              }
            >
              Request selected to unmute
            </button>
          </div>
          <span className="live-interview-roster-mute-select-count">
            {bulkMuteSelection.length} selected
          </span>
        </div>
      ) : null}

      {/* In-meeting section */}
      <div className="li-people-section">
        <div className="li-people-section-head">
          <span className="li-people-section-title">In meeting</span>
          <span className="li-people-section-count">{filteredRows.length}</span>
        </div>
        {filteredRows.length === 0 ? (
          <div className="li-people-empty">
            <Users size={20} aria-hidden />
            <span>No participants yet</span>
          </div>
        ) : (
          <ul className="li-people-list">
            {filteredRows.map(({ participant, role, label, isOperationalHost, isCoHost, initials }) => {
              const canRemove = canManageParticipants && !participant.isLocal && !isOperationalHost && !isCoHost;
              const canMuteThisCandidate =
                canMuteCandidates && !participant.isLocal && role === "candidate";
              const isSelf = participant.isLocal;
              const micOn = participant.isMicrophoneEnabled;
              const camOn = participant.isCameraEnabled;
              const speaking = participant.isSpeaking;
              const isSelected = bulkMuteSelection.includes(participant.identity);
              const statusText = isSelf
                ? "You"
                : speaking
                  ? "Speaking…"
                  : !micOn
                    ? "Muted"
                    : "In the call";

              return (
                <li className="li-people-row" key={participant.identity}>
                  {canMuteCandidates ? (
                    canMuteThisCandidate ? (
                      <label
                        className="live-interview-roster-select-cell"
                        title={isSelected ? "Remove from selection" : "Select candidate"}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleBulkMuteSelect(participant.identity)}
                          aria-label={`Select ${label}`}
                        />
                      </label>
                    ) : (
                      <div className="live-interview-roster-select-spacer" aria-hidden />
                    )
                  ) : null}
                  <div
                    className={`li-people-avatar ${speaking ? "is-speaking" : ""}`}
                    aria-hidden
                  >
                    {initials}
                  </div>
                  <div className="li-people-meta">
                    <div className="li-people-name-line">
                      <strong className="li-people-name">{label}</strong>
                      {showParticipantRoleBadges
                        ? isOperationalHost ? (
                            <span className="li-people-role-badge is-host">
                              <ShieldCheck size={11} aria-hidden />
                              Host
                            </span>
                          ) : isCoHost ? (
                            <span className="li-people-role-badge is-cohost">
                              <ShieldCheck size={11} aria-hidden />
                              Co-host
                            </span>
                          ) : role === "interviewer" ? (
                            <span className="li-people-role-badge is-interviewer">
                              Interviewer
                            </span>
                          ) : null
                        : null}
                    </div>
                    <div className="li-people-status-line">
                      <span
                        className={`li-people-status-icon ${micOn ? "" : "is-off"}`}
                        title={micOn ? "Microphone on" : "Microphone off"}
                      >
                        {micOn ? <Mic size={11} /> : <MicOff size={11} />}
                      </span>
                      <span
                        className={`li-people-status-icon ${camOn ? "" : "is-off"}`}
                        title={camOn ? "Camera on" : "Camera off"}
                      >
                        {camOn ? <Video size={11} /> : <VideoOff size={11} />}
                      </span>
                      <span className="li-people-status-text">{statusText}</span>
                    </div>
                  </div>
                  {(canMuteThisCandidate || canRemove) && !isSelf ? (
                    <div className="li-people-row-actions">
                      {canMuteThisCandidate ? (
                        <button
                          type="button"
                          className={`li-people-icon-btn ${micOn ? "" : "is-active"}`}
                          onClick={() => {
                            void handleCandidateAudioAction(
                              participant.identity,
                              micOn,
                              label
                            );
                          }}
                          disabled={
                            muteCandidateBusyIdentity === participant.identity ||
                            unmuteRequestBusyKey === `single:${participant.identity}` ||
                            unmuteRequestBusyKey === "selected" ||
                            unmuteRequestBusyKey === "all"
                          }
                          title={micOn ? "Mute candidate" : "Request candidate to unmute"}
                          aria-label={micOn ? "Mute candidate" : "Request candidate to unmute"}
                        >
                          {micOn ? <MicOff size={15} /> : <Mic size={15} />}
                        </button>
                      ) : null}
                      {canRemove ? (
                        <button
                          type="button"
                          className="li-people-icon-btn is-danger"
                          onClick={() => onRemoveParticipant(participant.identity)}
                          disabled={removeBusyIdentity === participant.identity}
                          title="Remove from meeting"
                          aria-label="Remove from meeting"
                        >
                          <UserMinus size={15} />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {joinRequestState && filteredPendingRequests.length > 0 ? (
        <div className="li-people-section li-people-section-waiting">
          <div className="li-people-section-head">
            <span className="li-people-section-title">Waiting to join</span>
            <span className="li-people-section-count is-attention">
              {filteredPendingRequests.length}
            </span>
          </div>
          <ul className="li-people-list">
            {filteredPendingRequests.map((request) => {
              const name = request.display_name || "Candidate";
              return (
                <li className="li-people-row li-people-row-waiting" key={request.request_id}>
                  <div className="li-people-avatar is-waiting" aria-hidden>
                    {name.charAt(0).toUpperCase()}
                  </div>
                  <div className="li-people-meta">
                    <div className="li-people-name-line">
                      <strong className="li-people-name">{name}</strong>
                    </div>
                    {request.email ? (
                      <div className="li-people-status-line">
                        <span className="li-people-status-text">{request.email}</span>
                      </div>
                    ) : null}
                  </div>
                  <div className="li-people-row-actions">
                    <button
                      type="button"
                      className="li-people-admit-btn is-deny"
                      onClick={() => joinRequestState.onRespond?.(request.request_id, "denied")}
                      disabled={joinRequestState.busyRequestId === request.request_id}
                      title="Deny request"
                    >
                      Deny
                    </button>
                    <button
                      type="button"
                      className="li-people-admit-btn is-admit"
                      onClick={() => joinRequestState.onRespond?.(request.request_id, "approved")}
                      disabled={joinRequestState.busyRequestId === request.request_id}
                      title="Admit to meeting"
                    >
                      Admit
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          {joinRequestState.error ? (
            <div className="li-people-error">{joinRequestState.error}</div>
          ) : null}
        </div>
      ) : null}

      {removeError ? <div className="live-interview-error">{removeError}</div> : null}
    </div>
  );
};

const RecordingLibrarySidePanel = ({
  retentionDays,
  recordings,
  recordingLive,
  recordingProcessing,
  recordingDurationLabel,
  onPlayOrDownload,
  onViewTranscriptPdf,
  recordingDownloadBusyId,
  transcriptPdfEligible,
  transcriptExportStatus,
  transcriptPdfAvailable,
  transcriptPdfBusy,
  recordingError,
  // Host control props — when present, the primary Start/Stop button renders.
  canManageRecording = false,
  recordingBusy = false,
  onToggleRecording,
}) => {
  const buttonLabel = recordingBusy
    ? recordingLive
      ? "Stopping…"
      : "Starting…"
    : recordingLive
      ? "Stop recording"
      : "Start recording";

  return (
    <div className="li-rec-panel">
      {/* Recordings panel is intentionally just the Start/Stop control — no
          status text, transcript export, or completed-recordings list. Host
          only; candidates see nothing actionable here. */}
      {canManageRecording ? (
        <>
          <button
            type="button"
            className={`li-rec-status-cta ${recordingLive ? "is-stop" : "is-start"}`}
            onClick={onToggleRecording}
            disabled={recordingBusy || recordingProcessing}
          >
            {recordingLive ? (
              <Square size={15} aria-hidden />
            ) : (
              <Radio size={15} aria-hidden />
            )}
            {buttonLabel}
          </button>
          {recordingError ? (
            <div className="li-rec-status-error">{recordingError}</div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const LiveInterviewDrawer = ({
  activePanel,
  onClose,
  transcriptProps,
  chatState,
  chatPersistProps,
  participantRosterProps,
  recordingsLibraryProps,
}) => {
  if (!activePanel) return null;

  const isTranscript = activePanel === "transcript";
  const isChat = activePanel === "chat";
  const isPeople = activePanel === "participants";
  const isRecordings = activePanel === "recordings";
  const hasAiCompanion = isTranscript && Boolean(transcriptProps?.applicationContextProps);

  if (isRecordings && !recordingsLibraryProps) return null;

  const title = isRecordings
    ? "Recordings"
    : isTranscript
      ? hasAiCompanion
        ? "Interview assistant"
        : "Live notes and speech"
      : isChat
        ? ""
        : "People";
  const kicker = isRecordings
    ? "Library"
    : isTranscript
      ? hasAiCompanion
        ? "Meeting tools"
        : "Transcript"
      : isChat
        ? "Chat"
        : "Participants";

  const drawerKind = isRecordings
    ? "recordings"
    : isPeople
      ? "people"
      : isTranscript
        ? "transcript"
        : "chat";

  return (
    <aside
      className={`live-interview-drawer ${drawerKind}`}
      aria-label={`${kicker} panel`}
    >
      <div className="live-interview-drawer-header">
        <div>
          <p className="live-interview-kicker">{kicker}</p>
          {title ? <h2>{title}</h2> : null}
        </div>
        <button
          type="button"
          className="li-drawer-close-btn"
          onClick={onClose}
          aria-label="Close panel"
          title="Close"
        >
          <X size={18} strokeWidth={2.25} aria-hidden />
        </button>
      </div>
      <div className="live-interview-drawer-body">
        {isRecordings ? <RecordingLibrarySidePanel {...recordingsLibraryProps} /> : null}
        {isTranscript ? <TranscriptPanel {...transcriptProps} /> : null}
        {isChat ? <LiveInterviewChatPanel {...chatState} {...chatPersistProps} /> : null}
        {isPeople ? <ParticipantRosterPanel {...participantRosterProps} /> : null}
      </div>
    </aside>
  );
};

const HostJoinRequests = ({ requests, busyRequestId, error, isOpen, onClose, onRespond, inline = false }) => {
  const pendingRequests = getPendingRequests(requests);
  if (!pendingRequests.length) {
    if (inline) {
      return error ? (
        <div className={`live-interview-admit-panel inline li-waiting-notify`}>
          <div className="live-interview-error">{error}</div>
        </div>
      ) : null;
    }
    if (error && isOpen) {
      return (
        <div className="live-interview-admit-panel li-waiting-notify" role="alert">
          <div className="live-interview-error">{error}</div>
        </div>
      );
    }
    return null;
  }

  if (!inline && !isOpen) return null;

  const waitingCount = pendingRequests.length;
  const waitingLabel = waitingCount === 1 ? "1 waiting" : `${waitingCount} waiting`;

  return (
    <div
      className={`live-interview-admit-panel li-waiting-notify ${inline ? "inline" : ""}`}
      role="status"
      aria-live="polite"
      aria-label={`${waitingLabel}. ${pendingRequests[0]?.display_name || "Candidate"} is waiting to join.`}
    >
      <div className="live-interview-admit-header">
        <div>
          <strong>Waiting Room</strong>
          <span className="li-waiting-notify-count">{waitingLabel}</span>
        </div>
        {onClose && !inline ? (
          <button
            type="button"
            className="li-waiting-notify-close"
            onClick={onClose}
            aria-label="Dismiss waiting notification"
          >
            <X size={14} />
          </button>
        ) : null}
      </div>
      {pendingRequests.map((request) => {
        const name = String(request.display_name || "Candidate").trim() || "Candidate";
        const busy = busyRequestId === request.request_id;
        const initial = name.charAt(0).toUpperCase();
        return (
          <div className={`live-interview-admit-row${busy ? " is-busy" : ""}`} key={request.request_id}>
            <div className="li-waiting-notify-person">
              <div className="li-waiting-notify-avatar" aria-hidden>
                {initial}
              </div>
              <div className="li-waiting-notify-copy">
                <strong>{name}</strong>
              </div>
            </div>
            <div className="live-interview-admit-actions">
              <button
                type="button"
                className="li-waiting-notify-btn is-deny"
                onClick={() => onRespond(request.request_id, "denied")}
                disabled={busy}
              >
                <UserX size={14} aria-hidden />
                Deny
              </button>
              <button
                type="button"
                className="li-waiting-notify-btn is-admit"
                onClick={() => onRespond(request.request_id, "approved")}
                disabled={busy}
              >
                <UserCheck size={14} aria-hidden />
                {busy ? "Admitting…" : "Admit"}
              </button>
            </div>
          </div>
        );
      })}
      {error ? <div className="live-interview-error">{error}</div> : null}
    </div>
  );
};

const MeetingToolbarButton = ({
  active = false,
  danger = false,
  compact = false,
  disabled = false,
  title,
  label,
  icon: Icon,
  badge = null,
  onClick,
  type = "button",
}) => (
  <button
    type={type}
    className={[
      "live-interview-toolbar-btn",
      active ? "is-active" : "",
      danger ? "is-danger" : "",
      compact ? "is-compact" : "",
    ]
      .filter(Boolean)
      .join(" ")}
    title={title || label}
    aria-label={label}
    aria-pressed={active ? "true" : undefined}
    disabled={disabled}
    onClick={onClick}
  >
    {Icon ? <Icon size={18} aria-hidden /> : null}
    <span className="live-interview-toolbar-btn-label">{label}</span>
    {badge}
  </button>
);

const LiveInterviewRoomContent = ({
  activePanel,
  onClosePanel,
  onOpenPanel,
  onUnreadChatCountChange,
  meetingWrapRef,
  displayName,
  meetingError,
  onLeave,
  leaveDisabled = false,
  leaveLabel = "Leave or end meeting",
  SettingsComponent = null,
  meetingMenuProps,
  transcriptProps,
  chatPersistProps,
  joinRequestState,
  participantRosterProps,
  recordingsLibraryProps,
  audioOutputDeviceId = "",
  browserOnline = true,
}) => {
  const room = useRoomContext();
  const chatState = useChat();
  const chatMessages = chatState?.chatMessages || [];
  const connectionState = useConnectionState();
  const [hasConnectedOnce, setHasConnectedOnce] = useState(false);
  useEffect(() => {
    if (connectionState === ConnectionState.Connected) {
      setHasConnectedOnce(true);
    }
  }, [connectionState]);
  const reconnectBannerMessage = getReconnectBannerMessage({
    connectionState,
    browserOnline,
    hasConnectedOnce,
  });
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const localIdentity = useMemo(
    () => String(localParticipant?.identity || "").trim(),
    [localParticipant?.identity]
  );
  const chatPersistWithIdentity = {
    ...(chatPersistProps || {}),
    localIdentity,
  };
  const [chatToast, setChatToast] = useState(null);
  const [chatToastHost, setChatToastHost] = useState(null);
  const [chatNotificationsReady, setChatNotificationsReady] = useState(false);
  const [unmuteRequestPrompt, setUnmuteRequestPrompt] = useState(null);
  const [unmuteRequestBusy, setUnmuteRequestBusy] = useState(false);
  const [unmuteRequestError, setUnmuteRequestError] = useState("");
  const chatToastTimerRef = useRef(null);
  const chatUnreadCountRef = useRef(0);
  const chatReadyRef = useRef(false);
  const chatBaselineTimerRef = useRef(null);
  const chatMessagesRef = useRef(chatMessages);
  const lastProcessedChatIndexRef = useRef(-1);
  const announcedChatKeysRef = useRef(new Set());

  useEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);

  useEffect(() => {
    const deviceId = String(audioOutputDeviceId || "").trim();
    if (!room || !deviceId || connectionState !== ConnectionState.Connected) {
      return undefined;
    }
    let cancelled = false;
    const applySelectedSpeaker = () => {
      if (cancelled) return;
      void applyAudioOutputToLiveKitRoom(room, deviceId).catch((error) => {
        if (!cancelled) {
          console.warn("[LiveInterview] Could not apply selected speaker", error);
        }
      });
    };
    applySelectedSpeaker();
    room.on(RoomEvent.TrackSubscribed, applySelectedSpeaker);
    room.on(RoomEvent.AudioPlaybackStatusChanged, applySelectedSpeaker);
    return () => {
      cancelled = true;
      room.off(RoomEvent.TrackSubscribed, applySelectedSpeaker);
      room.off(RoomEvent.AudioPlaybackStatusChanged, applySelectedSpeaker);
    };
  }, [audioOutputDeviceId, connectionState, room]);

  const resetChatNotificationState = useCallback(() => {
    chatReadyRef.current = false;
    setChatNotificationsReady(false);
    lastProcessedChatIndexRef.current = -1;
    announcedChatKeysRef.current = new Set();
    chatUnreadCountRef.current = 0;
    if (chatBaselineTimerRef.current) {
      window.clearTimeout(chatBaselineTimerRef.current);
      chatBaselineTimerRef.current = null;
    }
  }, []);

  const announceIncomingChatMessages = useCallback(
    (incomingMessages) => {
      const freshRemote = [];
      (incomingMessages || []).forEach((message, index) => {
        if (!message || isChatMessageFromSelf(message, localIdentity)) return;
        const cleanText = String(message?.message || "").trim();
        if (!cleanText) return;
        const dedupeKey = getChatMessageKey(message, index);
        const persistedKey = message?.id
          ? getPersistedChatRowKey({
              message_id: message.id,
              timestamp:
                message.timestamp instanceof Date
                  ? message.timestamp.toISOString()
                  : message.timestamp,
              sender_identity: message.from?.identity,
              message: cleanText,
            })
          : "";
        if (
          announcedChatKeysRef.current.has(dedupeKey) ||
          (persistedKey && announcedChatKeysRef.current.has(persistedKey))
        ) {
          return;
        }
        announcedChatKeysRef.current.add(dedupeKey);
        if (persistedKey) {
          announcedChatKeysRef.current.add(persistedKey);
        }
        freshRemote.push(message);
      });

      if (!freshRemote.length || activePanel === "chat") {
        return;
      }

      const count = freshRemote.length;
      const lastRemote = freshRemote[count - 1];
      const toastKey = getChatMessageKey(lastRemote, count - 1);
      const speaker = getChatSpeakerLabel(lastRemote);
      const preview = getChatNotificationPreview(lastRemote);

      chatUnreadCountRef.current += count;
      onUnreadChatCountChange?.(chatUnreadCountRef.current);

      const toastDurationMs = 7000;
      setChatToast((current) => {
        const previousShown = current ? Math.max(1, Number(current.extraCount) || 1) : 0;
        const total = previousShown + count;
        return {
          id: toastKey,
          speaker,
          preview,
          extraCount: Math.min(total, 99),
        };
      });

      if (chatToastTimerRef.current) {
        window.clearTimeout(chatToastTimerRef.current);
      }
      chatToastTimerRef.current = window.setTimeout(() => {
        setChatToast((current) => (current?.id === toastKey ? null : current));
        chatToastTimerRef.current = null;
      }, toastDurationMs);

      const shouldUseBrowserNotification =
        typeof window !== "undefined" &&
        "Notification" in window &&
        window.Notification.permission === "granted" &&
        (document.visibilityState === "hidden" || !document.hasFocus?.());

      if (shouldUseBrowserNotification) {
        try {
          const notification = new window.Notification(`${speaker} messaged you`, {
            body: preview,
            tag: "live-interview-chat",
          });
          notification.onclick = () => {
            window.focus?.();
            onOpenPanel?.("chat");
            notification.close();
          };
        } catch (error) {
          console.warn("[LiveInterview] Browser notification failed", error);
        }
      }
    },
    [activePanel, localIdentity, onOpenPanel, onUnreadChatCountChange]
  );

  useLayoutEffect(() => {
    const host = meetingWrapRef?.current;
    if (host && host !== chatToastHost) {
      setChatToastHost(host);
    }
  }, [chatToast, chatToastHost, meetingWrapRef, connectionState]);

  useEffect(() => {
    if (!room || !localIdentity) {
      return undefined;
    }

    const onDataReceived = (payload, _sender, _kind, topic) => {
      if (topic && topic !== UNMUTE_REQUEST_TOPIC) return;
      const parsed = parseUnmuteRequestDataPayload(payload);
      if (!parsed?.targetIdentities?.length) return;
      if (!parsed.targetIdentities.includes(localIdentity)) return;
      if (parsed.sender && parsed.sender === localIdentity) return;
      if (isMicrophoneEnabled) return;

      const senderParticipant =
        room.localParticipant?.identity === parsed.sender
          ? room.localParticipant
          : room.getParticipantByIdentity(parsed.sender);
      const senderName =
        String(parsed.name || "").trim() ||
        resolveParticipantDisplayName(senderParticipant) ||
        "Interviewer";

      setUnmuteRequestBusy(false);
      setUnmuteRequestError("");
      setUnmuteRequestPrompt({
        id: `${Date.now()}-${parsed.sender || "staff"}`,
        senderName,
      });

      const shouldUseBrowserNotification =
        typeof window !== "undefined" &&
        "Notification" in window &&
        window.Notification.permission === "granted" &&
        (document.visibilityState === "hidden" || !document.hasFocus?.());

      if (shouldUseBrowserNotification) {
        try {
          const notification = new window.Notification(
            `${senderName} requested you to unmute`,
            {
              body: "Open the meeting and turn on your microphone.",
              tag: `live-interview-unmute-${localIdentity}`,
            }
          );
          notification.onclick = () => {
            window.focus?.();
            notification.close();
          };
        } catch (error) {
          console.warn("[LiveInterview] Browser notification failed", error);
        }
      }
    };

    room.on(RoomEvent.DataReceived, onDataReceived);
    return () => {
      room.off(RoomEvent.DataReceived, onDataReceived);
    };
  }, [isMicrophoneEnabled, localIdentity, room]);

  useEffect(() => {
    if (!isMicrophoneEnabled) return;
    setUnmuteRequestPrompt(null);
    setUnmuteRequestBusy(false);
    setUnmuteRequestError("");
  }, [isMicrophoneEnabled]);

  const handleAcceptUnmuteRequest = useCallback(async () => {
    const participantController = room?.localParticipant || localParticipant;
    if (!participantController || typeof participantController.setMicrophoneEnabled !== "function") {
      setUnmuteRequestError("Microphone controls are not ready yet. Please use the mic button.");
      return;
    }

    setUnmuteRequestBusy(true);
    setUnmuteRequestError("");
    try {
      await participantController.setMicrophoneEnabled(true);
      setUnmuteRequestPrompt(null);
    } catch (error) {
      console.warn("[LiveInterview] Could not enable microphone from unmute request", error);
      setUnmuteRequestError("Could not turn on your microphone. Please use the mic button.");
    } finally {
      setUnmuteRequestBusy(false);
    }
  }, [localParticipant, room]);

  useEffect(() => {
    return () => {
      if (chatToastTimerRef.current) {
        window.clearTimeout(chatToastTimerRef.current);
      }
      if (chatBaselineTimerRef.current) {
        window.clearTimeout(chatBaselineTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (activePanel === "chat") {
      chatUnreadCountRef.current = 0;
      onUnreadChatCountChange?.(0);
      setChatToast(null);
      if (chatToastTimerRef.current) {
        window.clearTimeout(chatToastTimerRef.current);
        chatToastTimerRef.current = null;
      }
    }
  }, [activePanel, onUnreadChatCountChange]);

  useEffect(() => {
    if (connectionState === ConnectionState.Disconnected) {
      resetChatNotificationState();
      onUnreadChatCountChange?.(0);
      setChatToast(null);
      return undefined;
    }

    if (connectionState !== ConnectionState.Connected || chatReadyRef.current) {
      return undefined;
    }

    chatBaselineTimerRef.current = window.setTimeout(() => {
      const snapshot = chatMessagesRef.current || [];
      chatReadyRef.current = true;
      setChatNotificationsReady(true);
      lastProcessedChatIndexRef.current = snapshot.length - 1;
      snapshot.forEach((message, index) => {
        announcedChatKeysRef.current.add(getChatMessageKey(message, index));
      });
      chatBaselineTimerRef.current = null;
    }, 600);

    return () => {
      if (chatBaselineTimerRef.current) {
        window.clearTimeout(chatBaselineTimerRef.current);
        chatBaselineTimerRef.current = null;
      }
    };
  }, [connectionState, onUnreadChatCountChange, resetChatNotificationState]);

  useEffect(() => {
    if (!chatReadyRef.current) {
      return;
    }

    if (chatMessages.length - 1 < lastProcessedChatIndexRef.current) {
      lastProcessedChatIndexRef.current = chatMessages.length - 1;
      return;
    }

    const start = lastProcessedChatIndexRef.current + 1;
    if (start >= chatMessages.length) {
      return;
    }

    const batch = [];
    for (let index = start; index < chatMessages.length; index += 1) {
      batch.push(chatMessages[index]);
    }
    lastProcessedChatIndexRef.current = chatMessages.length - 1;
    announceIncomingChatMessages(batch);
  }, [announceIncomingChatMessages, chatMessages]);

  useEffect(() => {
    const safeRoomId = String(chatPersistProps?.roomId || "").trim();
    if (!safeRoomId || !chatNotificationsReady || connectionState !== ConnectionState.Connected) {
      return undefined;
    }

    let cancelled = false;

    const pollPersistedChat = async () => {
      if (cancelled) {
        return;
      }
      try {
        const { listLiveInterviewChatMessages } = await import("../api/liveInterviewApi");
        const payload = await listLiveInterviewChatMessages(safeRoomId, {
          actorEmail: chatPersistProps?.actorEmail,
          actorIdentity: chatPersistProps?.actorIdentity,
          role: chatPersistProps?.actorRole || "interviewer",
          inviteToken: chatPersistProps?.inviteToken,
        });
        if (cancelled) {
          return;
        }

        const rows = payload?.messages || [];
        const freshRows = [];
        rows.forEach((row) => {
          if (isPersistedChatRowFromSelf(row, localIdentity)) {
            return;
          }
          if (!String(row?.message || "").trim()) {
            return;
          }
          const rowKey = getPersistedChatRowKey(row);
          if (announcedChatKeysRef.current.has(rowKey)) {
            return;
          }
          freshRows.push(toPersistedChatEntry(row));
        });

        if (freshRows.length) {
          announceIncomingChatMessages(freshRows);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("[LiveInterview] Chat notification poll failed", error);
        }
      }
    };

    void pollPersistedChat();
    const intervalId = window.setInterval(pollPersistedChat, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    announceIncomingChatMessages,
    chatPersistProps?.actorEmail,
    chatPersistProps?.actorIdentity,
    chatPersistProps?.actorRole,
    chatPersistProps?.inviteToken,
    chatPersistProps?.roomId,
    chatNotificationsReady,
    connectionState,
    localIdentity,
  ]);

  const openChatFromToast = () => {
    onOpenPanel?.("chat");
    setChatToast(null);
    if (chatToastTimerRef.current) {
      window.clearTimeout(chatToastTimerRef.current);
      chatToastTimerRef.current = null;
    }
  };

  const chatToastPreview = String(chatToast?.preview || "").trim();
  const chatToastPreviewShort =
    chatToastPreview.length > 88
      ? `${chatToastPreview.slice(0, 85)}…`
      : chatToastPreview;
  const chatToastSpeaker = String(chatToast?.speaker || "Someone").trim() || "Someone";
  const chatToastExtra = Math.max(0, Number(chatToast?.extraCount) || 0);
  const chatToastCue = chatToastExtra > 1 ? `${chatToastExtra} new messages` : "New message";
  const waitingOverlayVisible = Boolean(
    joinRequestState?.isOpen && getPendingRequests(joinRequestState?.requests).length
  );

  const chatToastNode = chatToast ? (
    <div
      className={`li-chat-notify${waitingOverlayVisible ? " is-below-waiting" : ""}`}
      role="button"
      tabIndex={0}
      aria-live="polite"
      title={`${chatToastSpeaker}: ${chatToastPreviewShort || "sent a message"}`}
      aria-label={`${chatToastCue}. ${chatToastSpeaker}. Open chat.`}
      onClick={openChatFromToast}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openChatFromToast();
        }
      }}
    >
      <div className="li-chat-notify-icon" aria-hidden>
        <MessageSquare size={16} strokeWidth={2.1} />
      </div>
      <div className="li-chat-notify-copy">
        <span className="li-chat-notify-cue">{chatToastCue}</span>
        <strong>{chatToastSpeaker}</strong>
        <span className="li-chat-notify-preview">{chatToastPreviewShort || "sent a message"}</span>
      </div>
      <button
        type="button"
        className="li-chat-notify-close"
        onClick={(event) => {
          event.stopPropagation();
          setChatToast(null);
          if (chatToastTimerRef.current) {
            window.clearTimeout(chatToastTimerRef.current);
            chatToastTimerRef.current = null;
          }
        }}
        aria-label="Dismiss chat notification"
      >
        <X size={12} strokeWidth={2} />
      </button>
    </div>
  ) : null;
  const unmuteRequestToastNode = unmuteRequestPrompt ? (
    <div
      className="li-unmute-notify"
      role="dialog"
      aria-live="polite"
      aria-labelledby="live-interview-unmute-request-title"
      aria-describedby="live-interview-unmute-request-copy"
    >
      <div className="li-unmute-notify-icon" aria-hidden>
        <Mic size={16} strokeWidth={2.1} />
      </div>
      <div className="li-unmute-notify-body">
        <div className="li-unmute-notify-copy">
          <span className="li-unmute-notify-cue">Unmute request</span>
          <strong id="live-interview-unmute-request-title">
            {unmuteRequestPrompt.senderName}
          </strong>
          <span id="live-interview-unmute-request-copy" className="li-unmute-notify-preview">
            Turn on your microphone when you are ready to speak.
          </span>
          {unmuteRequestError ? (
            <span className="li-unmute-notify-error">{unmuteRequestError}</span>
          ) : null}
        </div>
        <div className="li-unmute-notify-actions">
          <button
            type="button"
            className="li-unmute-notify-btn is-secondary"
            onClick={() => {
              setUnmuteRequestPrompt(null);
              setUnmuteRequestError("");
              setUnmuteRequestBusy(false);
            }}
          >
            Not now
          </button>
          <button
            type="button"
            className="li-unmute-notify-btn is-primary"
            onClick={() => {
              void handleAcceptUnmuteRequest();
            }}
            disabled={unmuteRequestBusy}
          >
            <Mic size={14} strokeWidth={2.2} />
            {unmuteRequestBusy ? "Turning on..." : "Unmute"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const toastMountNode = chatToastHost || meetingWrapRef?.current || null;

  return (
    <>
      <LiveTranscriptCapture {...transcriptProps} />
      {chatToastNode && toastMountNode ? createPortal(chatToastNode, toastMountNode) : chatToastNode}
      {unmuteRequestToastNode && toastMountNode
        ? createPortal(unmuteRequestToastNode, toastMountNode)
        : unmuteRequestToastNode}
      <div className="live-interview-video-shell">
        {reconnectBannerMessage ? (
          <div className="li-reconnect-banner" role="status" aria-live="polite">
            <span className="li-reconnect-banner-dot" aria-hidden />
            <span>{reconnectBannerMessage}</span>
          </div>
        ) : null}
        <LiveInterviewMeetingRecordingBar
          canManageRecording={Boolean(recordingsLibraryProps?.canManageRecording)}
          recordingLive={Boolean(
            recordingsLibraryProps?.recordingLive ?? meetingMenuProps?.recordingLive,
          )}
          recordingProcessing={Boolean(recordingsLibraryProps?.recordingProcessing)}
          recordingDurationLabel={
            recordingsLibraryProps?.recordingDurationLabel ||
            meetingMenuProps?.recordingDurationLabel ||
            ""
          }
          recordingBusy={Boolean(recordingsLibraryProps?.recordingBusy)}
          recordingError={recordingsLibraryProps?.recordingError || ""}
          onToggleRecording={recordingsLibraryProps?.onToggleRecording}
        />
        {meetingError ? (
          <div className="live-interview-meeting-error">
            {meetingError}
          </div>
        ) : null}
        <HostJoinRequests {...joinRequestState} />
        <LocalParticipantFallback />
        <LiveInterviewVideoConference
          onLeave={onLeave}
          leaveDisabled={leaveDisabled}
          leaveLabel={leaveLabel}
          meetingMenuProps={meetingMenuProps}
          SettingsComponent={SettingsComponent}
        />
      </div>
      <LiveInterviewDrawer
        activePanel={activePanel}
        onClose={onClosePanel}
        transcriptProps={transcriptProps}
        chatState={chatState}
        chatPersistProps={chatPersistWithIdentity}
        participantRosterProps={participantRosterProps}
        recordingsLibraryProps={recordingsLibraryProps}
      />
    </>
  );
};

const RoomPage = () => {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken =
    searchParams.get("inviteToken") ||
    searchParams.get("invite_token") ||
    searchParams.get("token") ||
    "";
  // External interviewers (not logged into SearchJob) open the meeting room from
  // an email link that includes ?interviewer_token=... We exchange that token
  // for the verified interviewer context so the SPA can act as host on the
  // existing live-interview API surface (host_user_id matches this email).
  const interviewerTokenFromUrl =
    searchParams.get("interviewer_token") ||
    searchParams.get("interviewerToken") ||
    "";
  const candidateNameFromUrl = searchParams.get("name") || "";
  const consentAcceptedFromUrl = ["1", "true", "yes"].includes(
    String(searchParams.get("consent") || searchParams.get("recording_consent") || "")
      .trim()
      .toLowerCase()
  );
  const liveMeetBookingIdFromUrl = (searchParams.get("booking_id") || "").trim();
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenState, setTokenState] = useState(null);
  const [error, setError] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState("idle");
  const [recordingBusy, setRecordingBusy] = useState(false);
  const [recordingError, setRecordingError] = useState("");
  const [recordingJoinPromptVisible, setRecordingJoinPromptVisible] = useState(false);
  const [transcriptBusy, setTranscriptBusy] = useState(false);
  const [transcriptError, setTranscriptError] = useState("");
  const [meetingError, setMeetingError] = useState("");
  const [browserOnline, setBrowserOnline] = useState(
    typeof window === "undefined" ? true : window.navigator?.onLine !== false
  );
  const [networkDetail, setNetworkDetail] = useState("");
  const [liveSessionState, setLiveSessionState] = useState("lobby");
  const [hasJoinedMeetingOnce, setHasJoinedMeetingOnce] = useState(false);
  const [activePanel, setActivePanel] = useState("");
  // The staff transcript drawer opens at most once for each room page session.
  // Keeping this outside React state means reconnects and routine renders do
  // not override an interviewer's explicit close action.
  const autoOpenedTranscriptRoomRef = useRef("");
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pendingJoinRequests, setPendingJoinRequests] = useState([]);
  const [joinRequestBusyId, setJoinRequestBusyId] = useState("");
  const [joinRequestError, setJoinRequestError] = useState("");
  const [waitingPanelOpen, setWaitingPanelOpen] = useState(false);
  const [lobbyPresenceExpanded, setLobbyPresenceExpanded] = useState(false);
  const [participantRemoveBusyIdentity, setParticipantRemoveBusyIdentity] = useState("");
  const [participantRemoveError, setParticipantRemoveError] = useState("");
  const [muteAllBusy, setMuteAllBusy] = useState(false);
  const [muteCandidateBusyIdentity, setMuteCandidateBusyIdentity] = useState("");
  const [muteControlsError, setMuteControlsError] = useState("");
  const [muteAllConfirmOpen, setMuteAllConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [leaveExitModalOpen, setLeaveExitModalOpen] = useState(false);
  const [hostActionToast, setHostActionToast] = useState("");
  const [endingMeeting, setEndingMeeting] = useState(false);
  const [leavingMeeting, setLeavingMeeting] = useState(false);
  const [candidateInRoom, setCandidateInRoom] = useState(false);
  const [postMeetingFeedbackActive, setPostMeetingFeedbackActive] = useState(false);
  const [feedbackChoiceOpen, setFeedbackChoiceOpen] = useState(false);
  const [feedbackWizardOpen, setFeedbackWizardOpen] = useState(false);
  const [feedbackAccessToken, setFeedbackAccessToken] = useState("");
  const [feedbackFlowBusy, setFeedbackFlowBusy] = useState(false);
  const [feedbackFlowError, setFeedbackFlowError] = useState("");
  const hostToastTimerRef = useRef(null);
  const candidateRecordingLiveRef = useRef(false);
  // Keep the preview private until the participant explicitly enables a device.
  const [preJoinCameraOn, setPreJoinCameraOn] = useState(false);
  const [preJoinMicOn, setPreJoinMicOn] = useState(false);
  const [audioOutputDeviceId, setAudioOutputDeviceId] = useState(() =>
    readStoredAudioOutputDeviceId()
  );
  const [preJoinAudioDeviceId, setPreJoinAudioDeviceId] = useState("");
  const [preJoinVideoDeviceId, setPreJoinVideoDeviceId] = useState("");
  const [recordingConsentAccepted, setRecordingConsentAccepted] = useState(consentAcceptedFromUrl);
  const [recordingDownloadBusyId, setRecordingDownloadBusyId] = useState("");
  const [transcriptPdfBusy, setTranscriptPdfBusy] = useState(false);
  const [candidateRequestId, setCandidateRequestId] = useState("");
  const [candidateRequestStatus, setCandidateRequestStatus] = useState("");
  const [candidateRequestBusy, setCandidateRequestBusy] = useState(false);
  const [candidateWaitingNotice, setCandidateWaitingNotice] = useState("");
  // True once the host/co-host has ended the interview for everyone. Drives a
  // terminal "interview ended" screen with NO rejoin option (the room is gone
  // server-side, so offering reconnect would be misleading + always fail).
  const [sessionEndedByHost, setSessionEndedByHost] = useState(false);
  const sessionEndedByHostRef = useRef(false);
  // Mirrors whether the room is closed/ended per the latest room poll, read
  // synchronously inside the LiveKit disconnect handler.
  const roomHasEndedRef = useRef(false);
  const meetingPanelRef = useRef(null);
  const meetingWrapRef = useRef(null);
  const prevEffectiveJoinRequestIdRef = useRef(null);
  // Meeting elapsed clock (Google Meet / Zoom style). Set on LiveKit connect,
  // cleared on disconnect. Tick state forces re-render every second.
  const [meetingStartedAtMs, setMeetingStartedAtMs] = useState(null);
  const [meetingTick, setMeetingTick] = useState(0);
  const joinedRef = useRef(false);
  const seenWaitingNotifyIdsRef = useRef(new Set());
  const recordingJoinPromptDismissedRef = useRef(false);
  const joiningRef = useRef(false);
  const manualLeaveRequestedRef = useRef(false);
  const feedbackCompletedInMeetingRef = useRef(false);
  const feedbackAutoEmailQueuedKeysRef = useRef(new Set());
  /** True while the post-meeting feedback choice or wizard is open. */
  const postMeetingFeedbackActiveRef = useRef(false);
  const feedbackWizardOpenRef = useRef(false);
  /** Block auto-rejoin until Request to Join / Join now / Reconnect after leave, disconnect, or removal. */
  const candidateHoldLobbyAfterLeaveRef = useRef(false);
  const lastPolledJoinRequestStatusRef = useRef("");
  const candidateRequestStatusRef = useRef("");
  /** Candidate: LiveKit SDK may only connect after token success. */
  const [candidateLivekitSdkEnabled, setCandidateLivekitSdkEnabled] = useState(false);
  const [candidateLeaveInProgress, setCandidateLeaveInProgress] = useState(false);
  const [joinEligibility, setJoinEligibility] = useState(null);
  const [joinEligibilityLoading, setJoinEligibilityLoading] = useState(false);
  const [browserTranscriptStatus, setBrowserTranscriptStatus] = useState(null);
  const [transcriptMutation, setTranscriptMutation] = useState(null);
  const transcriptMutationEventIdRef = useRef(0);
  // Verified external-interviewer email returned by /api/meeting/booking/interviewer-context
  // when the user opened the room from an email link that carried
  // interviewer_token=... — this lets a non-SearchJob user host the meeting.
  const [externalInterviewerEmail, setExternalInterviewerEmail] = useState("");
  const [interviewerTokenVerifying, setInterviewerTokenVerifying] = useState(
    Boolean(interviewerTokenFromUrl)
  );
  const [interviewerTokenError, setInterviewerTokenError] = useState("");

  const role = inviteToken ? "candidate" : "interviewer";
  const isVerifiedInterviewerLobby =
    role !== "candidate" &&
    Boolean(externalInterviewerEmail) &&
    !interviewerTokenVerifying &&
    !interviewerTokenError;
  const joinRequestIdFromUrl = searchParams.get("joinRequestId") || searchParams.get("join_request_id") || "";
  const [instantGuestName, setInstantGuestName] = useState(() => {
    const preset = normalizeGuestDisplayName(candidateNameFromUrl);
    return isValidGuestDisplayName(preset) ? preset : "";
  });
  const [staffDisplayName, setStaffDisplayName] = useState("");
  const candidateInviteJoinMode = String(
    roomData?.candidate_invite?.join_mode || roomData?.invite?.join_mode || ""
  )
    .trim()
    .toLowerCase();
  const roomSchedulingMode = String(roomData?.room?.scheduling_mode || "")
    .trim()
    .toLowerCase();
  const isInstantOpenJoin = isOpenJoinLiveMeetRoom(roomData);
  const needsInstantGuestNameEntry =
    role === "candidate" &&
    isInstantOpenJoin &&
    !isValidGuestDisplayName(normalizeGuestDisplayName(candidateNameFromUrl));
  const instantGuestNameReady =
    !needsInstantGuestNameEntry || isValidGuestDisplayName(instantGuestName);
  const staffDisplayNameReady = isValidGuestDisplayName(
    normalizeGuestDisplayName(staffDisplayName)
  );
  const displayName = useMemo(
    () => {
      if (role === "candidate") {
        const fromUrl = normalizeGuestDisplayName(candidateNameFromUrl);
        const fromLobby = normalizeGuestDisplayName(instantGuestName);
        const fromRoom = normalizeGuestDisplayName(roomData?.room?.candidate_name);
        const fromInvite = normalizeGuestDisplayName(
          roomData?.candidate_invite?.participant_name ||
            roomData?.invite?.participant_name
        );
        if (isValidGuestDisplayName(fromUrl)) return fromUrl;
        if (isValidGuestDisplayName(fromLobby)) return fromLobby;
        if (isValidGuestDisplayName(fromRoom)) return fromRoom;
        if (isValidGuestDisplayName(fromInvite)) return fromInvite;
        return "Candidate";
      }
      const fromStaff = normalizeGuestDisplayName(staffDisplayName);
      if (isValidGuestDisplayName(fromStaff)) return fromStaff;
      return "";
    },
    [
      candidateNameFromUrl,
      instantGuestName,
      role,
      roomData?.candidate_invite?.participant_name,
      roomData?.invite?.participant_name,
      roomData?.room?.candidate_name,
      staffDisplayName,
    ]
  );
  const candidateAllowsDirectJoin =
    role === "candidate" &&
    (candidateInviteJoinMode === "direct" || isInstantOpenJoin);

  useEffect(() => {
    candidateRequestStatusRef.current = candidateRequestStatus;
  }, [candidateRequestStatus]);

  useEffect(() => {
    if (consentAcceptedFromUrl) {
      setRecordingConsentAccepted(true);
    }
  }, [consentAcceptedFromUrl]);

  const consentQueryValue = searchParams.get("consent");
  const joinRequestIdQueryValue =
    searchParams.get("joinRequestId") || searchParams.get("join_request_id") || "";
  useEffect(() => {
    if (role !== "candidate") return;
    const params = new URLSearchParams(searchParams);
    let changed = false;
    const nextConsent = recordingConsentAccepted ? "1" : "0";
    if (String(params.get("consent") || "") !== nextConsent) {
      params.set("consent", nextConsent);
      changed = true;
    }
    const nextId = String(candidateRequestId || "").trim();
    const currentId = String(
      params.get("joinRequestId") || params.get("join_request_id") || ""
    ).trim();
    if (nextId && nextId !== currentId) {
      params.set("joinRequestId", nextId);
      params.delete("join_request_id");
      changed = true;
    }
    if (!changed) return;
    navigate({ search: params.toString() }, { replace: true });
  }, [
    candidateRequestId,
    recordingConsentAccepted,
    role,
    consentQueryValue,
    joinRequestIdQueryValue,
    navigate,
    searchParams,
  ]);

  useEffect(() => {
    if (joined) {
      void requestBrowserNotificationPermission();
    }
  }, [joined]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const connection =
      window.navigator?.connection ||
      window.navigator?.mozConnection ||
      window.navigator?.webkitConnection ||
      null;

    const syncNetworkState = () => {
      setBrowserOnline(window.navigator?.onLine !== false);
      const detailParts = [];
      if (connection?.effectiveType) {
        detailParts.push(String(connection.effectiveType).toUpperCase());
      }
      if (Number.isFinite(connection?.downlink)) {
        detailParts.push(`${connection.downlink} Mbps`);
      }
      setNetworkDetail(detailParts.join(" | "));
    };

    syncNetworkState();
    window.addEventListener("online", syncNetworkState);
    window.addEventListener("offline", syncNetworkState);
    connection?.addEventListener?.("change", syncNetworkState);

    return () => {
      window.removeEventListener("online", syncNetworkState);
      window.removeEventListener("offline", syncNetworkState);
      connection?.removeEventListener?.("change", syncNetworkState);
    };
  }, []);

  useEffect(() => {
    if (role === "candidate") {
      setCandidateRequestId(joinRequestIdFromUrl);
      if (joinRequestIdFromUrl) {
        setCandidateRequestStatus((prev) => {
          if (["approved", "joined", "left", "removed", "rejected"].includes(prev)) {
            return prev;
          }
          return "pending";
        });
        setCandidateWaitingNotice((prev) => prev || getCandidateJoinStatusMessage("pending"));
      }
    }
  }, [joinRequestIdFromUrl, role]);

  // External interviewer flow: exchange the interviewer_token from the URL for
  // the verified host email. The same email is the room's host_user_id, so
  // every downstream live-interview API call that includes this as actor_email
  // is recognized as the host without a SearchJob session.
  useEffect(() => {
    if (!interviewerTokenFromUrl || role === "candidate") {
      return undefined;
    }
    let cancelled = false;
    setInterviewerTokenVerifying(true);
    setInterviewerTokenError("");
    (async () => {
      try {
        const payload = await fetchInterviewerContextByToken(interviewerTokenFromUrl);
        if (cancelled) return;
        const email = String(payload?.interviewer_email || "").trim();
        if (!email) {
          throw new Error("Interviewer email missing from verified context.");
        }
        setExternalInterviewerEmail(email);
      } catch (err) {
        if (cancelled) return;
        console.error("[LiveInterview] Interviewer token verification failed", err);
        setInterviewerTokenError(
          err?.message ||
            "This interviewer link is invalid or has expired. Please use the latest link from your email."
        );
      } finally {
        if (!cancelled) {
          setInterviewerTokenVerifying(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [interviewerTokenFromUrl, role]);

  // Read latest roomData via a ref so loadRoom / loadJoinEligibility do NOT
  // change identity each time we setRoomData. Previously their useCallback
  // deps included `roomData?.room`, which made every successful fetch produce
  // a new function reference, which invalidated the room-sync useEffect deps,
  // which immediately re-fired the fetch — a self-perpetuating loop that
  // looked like polling at 6-10 req/sec in the server log.
  const roomDataRef = useRef(roomData);
  useEffect(() => {
    roomDataRef.current = roomData;
  }, [roomData]);
  const tokenStateRef = useRef(tokenState);
  useEffect(() => {
    tokenStateRef.current = tokenState;
  }, [tokenState]);
  const externalInterviewerEmailRef = useRef(externalInterviewerEmail);
  useEffect(() => {
    externalInterviewerEmailRef.current = externalInterviewerEmail;
  }, [externalInterviewerEmail]);

  useEffect(() => {
    postMeetingFeedbackActiveRef.current = postMeetingFeedbackActive;
  }, [postMeetingFeedbackActive]);
  useEffect(() => {
    feedbackWizardOpenRef.current = feedbackWizardOpen;
  }, [feedbackWizardOpen]);

  const loadRoom = useCallback(
    async ({ silent = false } = {}) => {
      if (!roomId) return;
      if (!silent) {
        setLoading(true);
        setError("");
      }
      try {
        const payload = await getLiveInterviewRoom(roomId, {
          inviteToken,
          interviewerToken: role === "candidate" ? "" : interviewerTokenFromUrl,
          actorEmail:
            role === "candidate"
              ? ""
              : getCurrentUserEmail(
                  roomDataRef.current?.room,
                  externalInterviewerEmailRef.current
                ),
          actorIdentity:
            role === "candidate" ? "" : tokenStateRef.current?.identity || "",
          role,
        });
        setRoomData(payload);
      } catch (err) {
        console.error("[LiveInterview] Failed to load room", err);
        if (!silent) {
          setError(err.message || "Failed to load live interview room.");
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [inviteToken, interviewerTokenFromUrl, role, roomId]
  );

  const loadJoinEligibility = useCallback(async ({ silent = false } = {}) => {
    if (!roomId) return;
    if (!silent) {
      setJoinEligibilityLoading(true);
    }
    try {
      const payload = await getLiveInterviewJoinEligibility(roomId, {
        role,
        email:
          role === "candidate"
            ? ""
            : getCurrentUserEmail(
                roomDataRef.current?.room,
                externalInterviewerEmailRef.current
              ),
        invite_token: inviteToken,
      });
      setJoinEligibility(payload || null);
    } catch (err) {
      console.warn("[LiveInterview] Join eligibility check failed", err);
      const denied = err?.payload;
      if (
        denied &&
        typeof denied === "object" &&
        (denied.can_join === false || denied.reason || denied.message)
      ) {
        setJoinEligibility(denied);
      } else if (!silent) {
        setJoinEligibility(null);
      }
    } finally {
      if (!silent) {
        setJoinEligibilityLoading(false);
      }
    }
  }, [roomId, role, inviteToken]);

  useEffect(() => {
    // For an external interviewer arriving via the email link, wait until the
    // interviewer_token is verified before loading the room. Otherwise we'd
    // send actor_email="" and the backend would deny host access.
    if (interviewerTokenFromUrl && role !== "candidate" && !externalInterviewerEmail) {
      return;
    }
    loadRoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, inviteToken, externalInterviewerEmail, interviewerTokenFromUrl]);

  useEffect(() => {
    const activeClass = "live-interview-meeting-active";
    if (joined) {
      document.documentElement.classList.add(activeClass);
      document.body.classList.add(activeClass);
    } else {
      document.documentElement.classList.remove(activeClass);
      document.body.classList.remove(activeClass);
    }
    return () => {
      document.documentElement.classList.remove(activeClass);
      document.body.classList.remove(activeClass);
    };
  }, [joined]);

  useEffect(() => {
    if (!roomId || loading) return;
    loadJoinEligibility();
  }, [roomId, loading, loadJoinEligibility]);

  useEffect(() => {
    if (!roomId || joined || loading) {
      return undefined;
    }
    const pollPresence = () => {
      void loadJoinEligibility({ silent: true });
    };
    pollPresence();
    const timer = window.setInterval(pollPresence, 4000);
    return () => window.clearInterval(timer);
  }, [roomId, joined, loading, loadJoinEligibility]);

  useEffect(() => {
    candidateHoldLobbyAfterLeaveRef.current = false;
    setCandidateLivekitSdkEnabled(false);
    lastPolledJoinRequestStatusRef.current = "";
    setLiveSessionState("lobby");
  }, [roomId, inviteToken]);

  useEffect(() => {
    setRecordingStatus(normalizeRecordingStatus(roomData?.room?.recording_status));
  }, [roomData?.room?.recording_status]);

  useEffect(() => {
    if (!joined || !roomId) {
      return undefined;
    }
    const syncRoomSnapshot = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      void loadRoom({ silent: true });
    };
    syncRoomSnapshot();
    // Poll frequently enough that candidates leave promptly when LiveKit
    // delete_room fails but the DB already marks the room ended.
    const timer = window.setInterval(syncRoomSnapshot, 5000);
    return () => window.clearInterval(timer);
  }, [joined, loadRoom, roomId]);

  const hasActiveRoomRecording = Boolean(
    (roomData?.recordings || []).some((recording) =>
      ["recording", "active", "started", "in_progress", "live"].includes(
        String(recording?.status || "").trim().toLowerCase()
      )
    )
  );
  const normalizedRecordingStatus = normalizeRecordingStatus(recordingStatus);
  const recordingProcessing =
    normalizedRecordingStatus === "processing" ||
    (!hasActiveRoomRecording &&
      (roomData?.recordings || []).some((recording) =>
        ["processing", "stopping", "uploading", "finalizing"].includes(
          String(recording?.status || "").trim().toLowerCase()
        )
      ));
  const recordingTranscriptProcessing = Boolean(
    (roomData?.recordings || []).some((recording) =>
      ["processing", "pending", "queued", "in_progress"].includes(
        String(recording?.post_meeting_transcript_status || "").trim().toLowerCase()
      )
    )
  );
  const roomTranscriptExportProcessing = ["processing", "pending", "queued", "in_progress"].includes(
    String(roomData?.room?.post_meeting_transcript_status || "").trim().toLowerCase()
  );
  const transcriptPdfEligible = Boolean(
    roomData?.room?.post_meeting_transcript_pdf_available ||
    roomData?.room?.post_meeting_transcript_status ||
    roomData?.room?.ended_at ||
    String(roomData?.room?.status || "").trim().toLowerCase() === "closed"
  );
  const transcriptPdfAvailable = Boolean(roomData?.room?.post_meeting_transcript_pdf_available);
  const liveMeetBookingId = String(
    liveMeetBookingIdFromUrl || roomData?.room?.booking_id || ""
  ).trim();
  // The room is over when the backend reports a terminal status or an end
  // timestamp. Used to (a) flip everyone to the "interview ended" screen even
  // if the LiveKit disconnect reason isn't ROOM_DELETED, and (b) keep the
  // disconnect handler's synchronous ref fresh.
  const roomHasEnded = Boolean(
    roomData?.room?.ended_at ||
    ["ended", "closed", "completed"].includes(
      String(roomData?.room?.status || "").trim().toLowerCase()
    )
  );
  const meetingUiTerminated =
    (sessionEndedByHost || roomHasEnded) && !postMeetingFeedbackActive;
  const showLiveKitMeeting =
    joined &&
    Boolean(tokenState?.token && tokenState?.server_url) &&
    !meetingUiTerminated;

  useEffect(() => {
    roomHasEndedRef.current = roomHasEnded;
    if (roomHasEnded) {
      // Fallback for cases where the LiveKit disconnect fired before the poll
      // knew the room was ended (or never fired at all): once polling sees the
      // ended status, lock in the terminal screen and stop offering rejoin.
      sessionEndedByHostRef.current = true;
      setSessionEndedByHost(true);
      candidateHoldLobbyAfterLeaveRef.current = true;
      if (role !== "candidate" && postMeetingFeedbackActiveRef.current) {
        return;
      }
      if (joinedRef.current) {
        joinedRef.current = false;
        setJoined(false);
        setTokenState(null);
        setActivePanel("");
        if (role === "candidate") {
          setCandidateLivekitSdkEnabled(false);
          setCandidateWaitingNotice("");
        }
      }
    }
  }, [roomHasEnded, role]);

  useEffect(() => {
    if (!roomId || (!recordingProcessing && !recordingTranscriptProcessing && !roomTranscriptExportProcessing)) {
      return undefined;
    }
    const pollIntervalMs = recordingProcessing ? 4000 : 8000;
    const timer = window.setInterval(() => {
      void loadRoom({ silent: true });
    }, pollIntervalMs);
    return () => window.clearInterval(timer);
  }, [loadRoom, roomId, recordingProcessing, recordingTranscriptProcessing, roomTranscriptExportProcessing]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === meetingPanelRef.current);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const effectiveJoinRequestId = String(candidateRequestId || joinRequestIdFromUrl || "").trim();

  const recordingConsentAcceptedRef = useRef(recordingConsentAccepted);
  useEffect(() => {
    recordingConsentAcceptedRef.current = recordingConsentAccepted;
  }, [recordingConsentAccepted]);

  useEffect(() => {
    lastPolledJoinRequestStatusRef.current = "";
  }, [effectiveJoinRequestId]);

  useEffect(() => {
    if (prevEffectiveJoinRequestIdRef.current !== effectiveJoinRequestId) {
      prevEffectiveJoinRequestIdRef.current = effectiveJoinRequestId;
    }
  }, [effectiveJoinRequestId]);

  useEffect(() => {
    joinedRef.current = joined;
  }, [joined]);

  useEffect(() => {
    joiningRef.current = joining;
  }, [joining]);

  const requestToken = useCallback(
    async (requestIdOverride = "", options = {}) => {
      // Circuit-breaker: once the room is known to be ended/closed, NEVER fire
      // another join-token request. This is the hard stop that prevents the
      // ~10/sec 410 flood when a tab is left open on a meeting the host has
      // already ended — no matter which effect or handler calls this.
      if (sessionEndedByHostRef.current || roomHasEndedRef.current) {
        setLiveSessionState("ended");
        return false;
      }
      const { skipLivekitPrecheck = false, explicitCandidateJoin = false } = options;
      const lk = roomData?.livekit || {};
      const lkConfigured = Boolean(lk.configured);
      const lkReady = Boolean(lk.ready || (lkConfigured && lk.reachable !== false));
      const livekitDetailInner = lk.detail || "";
      if (!skipLivekitPrecheck && !lkReady) {
        setTokenError(livekitDetailInner || "LiveKit is not ready yet for this room.");
        return false;
      }
      if (role === "candidate" && !explicitCandidateJoin) {
        setTokenError(
          candidateAllowsDirectJoin
            ? "Use Join now to enter the video meeting."
            : "Request to join first. The interviewer must approve your request before you can enter."
        );
        return false;
      }
      const resolvedJoinRequestId = String(requestIdOverride || effectiveJoinRequestId || "").trim();
      if (role === "candidate" && !candidateAllowsDirectJoin && !resolvedJoinRequestId) {
        setTokenError("Request to join first. The interviewer must approve your request before you can enter.");
        return false;
      }
      setTokenError("");
      setMeetingError("");
      setLiveSessionState("connecting");
      setJoining(true);
      try {
        const payload = await requestLiveInterviewJoinToken(roomId, {
          role,
          display_name: displayName,
          email:
            role === "candidate"
              ? ""
              : getCurrentUserEmail(roomData?.room, externalInterviewerEmail),
          invite_token: inviteToken,
          join_request_id: candidateAllowsDirectJoin ? "" : resolvedJoinRequestId,
          recording_consent_accepted: role === "candidate" ? recordingConsentAccepted : true,
        });
        console.info("[LiveInterview] Join token ready", {
          roomId,
          role,
          isHost: payload.is_host,
          serverUrl: payload.server_url,
        });
        setTokenState(payload);
        setJoined(true);
        joinedRef.current = true;
        if (role === "candidate") {
          setCandidateLivekitSdkEnabled(true);
        }
        return true;
      } catch (err) {
        console.error("[LiveInterview] Failed to request join token", err);
        setTokenState(null);
        setJoined(false);
        joinedRef.current = false;
        if (role === "candidate") {
          setCandidateLivekitSdkEnabled(false);
        }

        // 410 Gone = the room has ended / been closed by the host. This is a
        // TERMINAL state — stop the auto-rejoin loop immediately, otherwise the
        // candidate's join effect keeps firing join-token ~10x/sec against a
        // dead room (the request flood you saw in the logs, which also jitters
        // the Activity popup that polls alongside it).
        const isRoomEnded =
          err?.status === 410 ||
          /has ended|interview has ended|room.*closed/i.test(
            String(err?.payload?.message || err?.message || "")
          );
        if (isRoomEnded) {
          sessionEndedByHostRef.current = true;
          setSessionEndedByHost(true);
          candidateHoldLobbyAfterLeaveRef.current = true;
          setCandidateWaitingNotice("");
          setTokenError("");
          setLiveSessionState("ended");
          return false;
        }

        if (role === "candidate" && err?.payload?.join_request?.status) {
          const nextStatus = normalizeJoinRequestStatus(err.payload.join_request.status);
          setCandidateRequestStatus(nextStatus);
          setCandidateWaitingNotice(getCandidateJoinStatusMessage(nextStatus));
        }
        setTokenError(
          err?.payload?.message || err.message || "LiveKit token is not available yet."
        );
        if (err?.payload?.can_join === false) {
          setJoinEligibility((prev) => ({ ...(prev || {}), ...err.payload }));
        }
        setLiveSessionState("error");
        return false;
      } finally {
        setJoining(false);
      }
    },
    [
      roomData?.livekit,
      roomData?.room,
      roomId,
      role,
      displayName,
      inviteToken,
      effectiveJoinRequestId,
      recordingConsentAccepted,
      candidateAllowsDirectJoin,
      externalInterviewerEmail,
    ]
  );
  const requestTokenRef = useRef(null);
  useEffect(() => {
    requestTokenRef.current = requestToken;
  }, [requestToken]);

  const room = roomData?.room;
  const transcriptRoomId = room?.room_id || tokenState?.room?.room_id || roomId;
  const livekitStatus = roomData?.livekit || {};
  const livekitConfigured = Boolean(livekitStatus.configured);
  const livekitReady = Boolean(livekitStatus.ready || (livekitConfigured && livekitStatus.reachable !== false));
  const lobbyTokenError = (() => {
    const rawTokenError = String(tokenError || "").trim();
    if (!rawTokenError) return "";
    if (
      /^(Use Join now|Request to join first|Ask to join first|Please enter your name|Please accept)/i.test(
        rawTokenError
      )
    ) {
      return rawTokenError;
    }
    const looksUnsafe = rawTokenError.length > 280 || /<|>|traceback/i.test(rawTokenError);
    return looksUnsafe
      ? "This interview room is not ready yet. Please try again shortly."
      : rawTokenError;
  })();
  const currentUserEmail = getCurrentUserEmail(room, externalInterviewerEmail);
  const interviewerDisplayNameStorageKey =
    role === "candidate"
      ? ""
      : getInterviewerDisplayNameStorageKey(roomId, currentUserEmail);
  const currentEmailMatchesRoom =
    normalizeEmail(currentUserEmail) &&
    [normalizeEmail(room?.host_user_id), normalizeEmail(room?.created_by_email)].includes(normalizeEmail(currentUserEmail));

  useEffect(() => {
    if (!interviewerDisplayNameStorageKey) return;
    const savedDisplayName = readInterviewerDisplayName(interviewerDisplayNameStorageKey);
    if (!isValidGuestDisplayName(savedDisplayName)) return;
    setStaffDisplayName((currentDisplayName) =>
      isValidGuestDisplayName(normalizeGuestDisplayName(currentDisplayName))
        ? currentDisplayName
        : savedDisplayName
    );
  }, [interviewerDisplayNameStorageKey]);

  useEffect(() => {
    if (!interviewerDisplayNameStorageKey || !staffDisplayNameReady) return;
    writeInterviewerDisplayName(
      interviewerDisplayNameStorageKey,
      normalizeGuestDisplayName(staffDisplayName)
    );
  }, [interviewerDisplayNameStorageKey, staffDisplayName, staffDisplayNameReady]);

  useEffect(() => {
    setBrowserTranscriptStatus(null);
    setTranscriptMutation(null);
  }, [transcriptRoomId]);
  const joinRequestActorEmail = currentUserEmail || "";
  const joinRequestActorIdentity = tokenState?.identity || "";
  const meetingManagerRole = String(tokenState?.role || role || "").trim().toLowerCase();
  const operationalHostEmail = normalizeEmail(room?.host_user_id || room?.created_by_email);
  const isHost = role !== "candidate" && Boolean(
    (operationalHostEmail && normalizeEmail(currentUserEmail) === operationalHostEmail) ||
    tokenState?.is_host
  );
  const canManageRecording =
    role !== "candidate" &&
    (Boolean(tokenState?.is_host) ||
      isHost ||
      ["admin", "host", "interviewer"].includes(meetingManagerRole));
  const canManageJoinRequests =
    role !== "candidate" &&
    joined &&
    (Boolean(tokenState?.is_host) ||
      isHost ||
      ["admin", "host", "interviewer"].includes(meetingManagerRole));
  const joinRequestManagerRole = isHost ? "host" : meetingManagerRole || role || "interviewer";
  const canManageCandidateInvites = joined && canManageJoinRequests && (
    isHost || ["admin", "host", "interviewer"].includes(meetingManagerRole)
  );
  const canInterviewTeamManageMeeting =
    role !== "candidate" &&
    joined &&
    (Boolean(tokenState?.is_host) ||
      isHost ||
      ["admin", "host", "interviewer"].includes(meetingManagerRole));
  // The person who can end the interview is offered the post-meeting feedback
  // flow. The feedback API remains the authorization boundary for creating or
  // submitting feedback; this UI flag must not depend on file-store metadata.
  const canManageInterviewFeedback =
    role !== "candidate" &&
    (Boolean(tokenState?.is_host) ||
      isHost ||
      ["admin", "host", "interviewer"].includes(meetingManagerRole));
  // Keep transcript capture running for the room, but only expose the transcript
  // UI to the interviewer side.
  const transcriptPanelAvailable = Boolean(transcriptRoomId) && role !== "candidate";
  const transcriptEnabled = room?.transcript_enabled !== false;
  const recordingLive =
    normalizedRecordingStatus === "recording" || hasActiveRoomRecording;
  useEffect(() => {
    if (!joined || role === "candidate" || !canManageRecording) {
      setRecordingJoinPromptVisible(false);
      return;
    }
    if (recordingLive || recordingProcessing) {
      setRecordingJoinPromptVisible(false);
      return;
    }
    if (recordingJoinPromptDismissedRef.current) {
      return;
    }
    setRecordingJoinPromptVisible(true);
  }, [joined, role, canManageRecording, recordingLive, recordingProcessing]);
  const retentionDays = roomData?.recording_compliance?.retention_days ?? 30;
  const mediaDeviceSupported = typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
  const [recordingAnchorMs, setRecordingAnchorMs] = useState(null);
  const [recordingTick, setRecordingTick] = useState(0);

  useEffect(() => {
    if (recordingLive) {
      setRecordingAnchorMs((prev) => (prev == null ? Date.now() : prev));
      const id = window.setInterval(() => setRecordingTick((t) => t + 1), 1000);
      return () => window.clearInterval(id);
    }
    setRecordingAnchorMs(null);
    return undefined;
  }, [recordingLive]);

  let recordingDurationLabel = null;
  if (recordingLive && recordingAnchorMs != null) {
    void recordingTick;
    const sec = Math.floor((Date.now() - recordingAnchorMs) / 1000);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    recordingDurationLabel = `${String(m).padStart(2, "0")} : ${String(s).padStart(2, "0")}`;
  }
  const candidateNeedsApproval = role === "candidate" && !candidateAllowsDirectJoin;
  const requiresJoinConsent = role === "candidate";
  const candidateJoinApproved = candidateRequestStatus === "approved";
  const candidateJoinPending = candidateRequestStatus === "pending";
  const candidateWasRemoved = role === "candidate" && candidateRequestStatus === "removed";
  const canRejoinSession =
    canRejoinDisconnectedSession(liveSessionState, meetingUiTerminated) ||
    canShowStaffRejoinLobby({
      role,
      liveSessionState,
      meetingUiTerminated,
      hasJoinedMeetingOnce,
    });
  const lobbyRejoinCopy = getLobbyRejoinCopy({ canRejoin: canRejoinSession, role });
  const candidateCanReconnect = role === "candidate" && canRejoinSession;
  const candidateInWaitingRoom =
    role === "candidate" &&
    candidateNeedsApproval &&
    candidateJoinPending &&
    !candidateCanReconnect &&
    !candidateWasRemoved;
  const candidateAdmittedAutoJoin =
    role === "candidate" &&
    candidateNeedsApproval &&
    candidateJoinApproved &&
    !candidateCanReconnect;
  const hideCandidateLobbyCta =
    candidateWasRemoved ||
    candidateInWaitingRoom ||
    (candidateAdmittedAutoJoin && recordingConsentAccepted && !tokenError);
  const candidateWaitingForAdmission =
    role === "candidate" &&
    candidateNeedsApproval &&
    !candidateJoinApproved &&
    !candidateCanReconnect &&
    !candidateWasRemoved;
  const joinPolicyBlocksRequestToJoin =
    joinEligibility != null &&
    (joinEligibility.can_request_join === false ||
      (joinEligibility.can_request_join == null && joinEligibility.can_join === false));
  const joinPolicyBlocksLivekitJoin =
    joinEligibility?.can_join === false &&
    (role === "candidate" || !currentEmailMatchesRoom);
  const joinPolicyBlocked = candidateWaitingForAdmission
    ? joinPolicyBlocksRequestToJoin
    : joinPolicyBlocksLivekitJoin;
  const joinEligibilityMessage = String(joinEligibility?.message || "").trim();
  const joinPolicyNotice =
    joinEligibilityMessage &&
    joinEligibilityMessage.length <= 280 &&
    !/[<>]|traceback/i.test(joinEligibilityMessage) &&
    (joinPolicyBlocked || joinEligibility?.can_join === false)
      ? joinEligibilityMessage
      : role === "candidate" && joinEligibilityLoading
        ? "Checking interview join window..."
        : "";
  const canTriggerLobbyPrimaryAction =
    role === "candidate"
      ? candidateWasRemoved
        ? false
        : candidateCanReconnect
        ? recordingConsentAccepted && (candidateNeedsApproval || (livekitReady && instantGuestNameReady))
        : candidateNeedsApproval
        ? recordingConsentAccepted && candidateJoinApproved
          ? true
          : recordingConsentAccepted && !candidateJoinPending
        : recordingConsentAccepted && livekitReady && instantGuestNameReady
      : livekitReady && staffDisplayNameReady;
  const canTriggerLobbyPrimaryActionSafe =
    canTriggerLobbyPrimaryAction &&
    !candidateLeaveInProgress &&
    !joinPolicyBlocked &&
    !meetingUiTerminated;
  const lobbyReadinessItems = [
    {
      key: "browser",
      icon: ShieldCheck,
      label: mediaDeviceSupported ? "Browser ready" : "Browser unavailable",
      tone: mediaDeviceSupported ? "ready" : "attention",
    },
  ];
  const inMeetingLobbyParticipants = useMemo(() => {
    const roster = Array.isArray(joinEligibility?.lobby_presence?.participants)
      ? joinEligibility.lobby_presence.participants
      : [];
    const seenParticipants = new Set();

    return roster.flatMap((participant) => {
      if (participant?.presence_state !== "in_meeting") return [];

      const displayName = String(participant?.display_name || "Participant").trim() || "Participant";
      const identity = String(participant?.identity || displayName).trim().toLowerCase();
      if (!identity || seenParticipants.has(identity)) return [];
      seenParticipants.add(identity);

      return [{ identity, displayName }];
    });
  }, [joinEligibility?.lobby_presence?.participants]);
  useEffect(() => {
    if (!inMeetingLobbyParticipants.length) {
      setLobbyPresenceExpanded(false);
    }
  }, [inMeetingLobbyParticipants.length]);
  const candidateLobbyNotice = useMemo(() => {
    if (joining && candidateRequestStatus === "approved") {
      return "Approved by host. Joining meeting...";
    }
    return candidateWaitingNotice;
  }, [joining, candidateRequestStatus, candidateWaitingNotice]);
  const candidateOptions = useMemo(() => {
    const options = [];
    const seenKeys = new Set();

    if (!isInstantOpenJoin) {
      mergeCandidateOption(
        options,
        seenKeys,
        room?.candidate_name,
        room?.candidate_email,
        "Primary candidate"
      );

      (roomData?.candidate_invites || []).forEach((invite) => {
        if (String(invite?.status || "").toLowerCase() === "revoked") {
          return;
        }
        mergeCandidateOption(
          options,
          seenKeys,
          invite?.participant_name || invite?.display_name,
          invite?.participant_email || invite?.email,
          "Existing invite"
        );
      });
    }

    (pendingJoinRequests || []).forEach((request) => {
      mergeCandidateOption(
        options,
        seenKeys,
        request?.display_name,
        request?.email,
        "Waiting room"
      );
    });

    return options;
  }, [
    isInstantOpenJoin,
    pendingJoinRequests,
    room?.candidate_email,
    room?.candidate_name,
    roomData?.candidate_invites,
  ]);

  const loadPendingJoinRequests = async () => {
    if (!canManageJoinRequests || !roomId) return;
    try {
      const payload = await getLiveInterviewJoinRequests(roomId, {
        actorEmail: joinRequestActorEmail,
        actorIdentity: joinRequestActorIdentity,
        role: joinRequestManagerRole,
        status: "pending",
      });
      setPendingJoinRequests(getPendingRequests(payload.requests || []));
      setJoinRequestError("");
    } catch (err) {
      console.error("[LiveInterview] Failed to load join requests", err);
      setJoinRequestError(getCleanJoinRequestError(err));
    }
  };

  useEffect(() => {
    if (!canManageJoinRequests) {
      setPendingJoinRequests([]);
      return undefined;
    }

    loadPendingJoinRequests();
    // 5s feels live enough for the host to admit waiting candidates without
    // being noticeable, while letting the backend breathe. (Was 2.5s.)
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void loadPendingJoinRequests();
    }, 5000);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageJoinRequests, roomId, joinRequestActorEmail, joinRequestActorIdentity]);

  useEffect(() => {
    if (!canManageJoinRequests) {
      setWaitingPanelOpen(false);
      seenWaitingNotifyIdsRef.current = new Set();
      return;
    }
    const ids = (pendingJoinRequests || [])
      .map((request) => String(request?.request_id || "").trim())
      .filter(Boolean);
    if (!ids.length) {
      setWaitingPanelOpen(false);
      seenWaitingNotifyIdsRef.current = new Set();
      return;
    }
    const hasNew = ids.some((id) => !seenWaitingNotifyIdsRef.current.has(id));
    ids.forEach((id) => seenWaitingNotifyIdsRef.current.add(id));
    if (hasNew) {
      setWaitingPanelOpen(true);
    }
  }, [canManageJoinRequests, pendingJoinRequests]);

  useEffect(() => {
    if (role !== "candidate" || !candidateNeedsApproval || !inviteToken || !effectiveJoinRequestId || joined) {
      return undefined;
    }
    if (candidateRequestStatusRef.current === "left" || candidateRequestStatusRef.current === "removed") {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const stopPolling = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const syncJoinRequestStatus = async () => {
      if (cancelled) return;
      try {
        const payload = await getLiveInterviewInviteJoinRequest(inviteToken, effectiveJoinRequestId);
        if (cancelled) return;
        const jr = payload?.request ?? payload?.join_request;
        const nextStatus = normalizeJoinRequestStatus(jr?.status);
        if (candidateHoldLobbyAfterLeaveRef.current && nextStatus === "approved") {
          return;
        }
        if (
          candidateHoldLobbyAfterLeaveRef.current &&
          nextStatus !== "approved" &&
          nextStatus !== "removed" &&
          nextStatus !== "left"
        ) {
          candidateHoldLobbyAfterLeaveRef.current = false;
        }
        if (nextStatus === "removed") {
          candidateHoldLobbyAfterLeaveRef.current = true;
        }
        const prevPolled = lastPolledJoinRequestStatusRef.current;
        lastPolledJoinRequestStatusRef.current = nextStatus;
        setCandidateRequestStatus(nextStatus);
        setCandidateWaitingNotice(getCandidateJoinStatusMessage(nextStatus));
        setTokenError("");
        if (
          nextStatus === "approved" &&
          prevPolled !== "approved" &&
          !candidateHoldLobbyAfterLeaveRef.current
        ) {
          queueMicrotask(() => {
            if (cancelled || joinedRef.current || candidateHoldLobbyAfterLeaveRef.current) return;
            if (!recordingConsentAcceptedRef.current) return;
            if (joiningRef.current) return;
            if (typeof requestTokenRef.current !== "function") return;
            void requestTokenRef.current("", { skipLivekitPrecheck: true, explicitCandidateJoin: true });
          });
        }
        if (nextStatus === "rejected" || nextStatus === "removed" || nextStatus === "joined" || nextStatus === "left") {
          stopPolling();
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[LiveInterview] Failed to load candidate join request status", err);
        setTokenError(err.message || "Unable to refresh your join request status.");
      }
    };

    syncJoinRequestStatus();
    // 4s feels responsive to the candidate when the host clicks Admit. Was 2s.
    intervalId = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void syncJoinRequestStatus();
    }, 4000);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void syncJoinRequestStatus();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [role, candidateNeedsApproval, inviteToken, effectiveJoinRequestId, joined]);

  /**
   * Host approved before consent: join as soon as the candidate accepts consent.
   */
  useEffect(() => {
    if (role !== "candidate" || !candidateNeedsApproval || joined || !inviteToken || !effectiveJoinRequestId) {
      return undefined;
    }
    if (candidateRequestStatus !== "approved" || !recordingConsentAccepted) {
      return undefined;
    }
    if (candidateHoldLobbyAfterLeaveRef.current || joining || joinedRef.current) {
      return undefined;
    }
    const t = window.setTimeout(() => {
      if (joinedRef.current || candidateHoldLobbyAfterLeaveRef.current) return;
      if (joiningRef.current) return;
      if (typeof requestTokenRef.current !== "function") return;
      void requestTokenRef.current("", { skipLivekitPrecheck: true, explicitCandidateJoin: true });
    }, 0);
    return () => window.clearTimeout(t);
  }, [
    role,
    candidateNeedsApproval,
    joined,
    joining,
    inviteToken,
    effectiveJoinRequestId,
    candidateRequestStatus,
    recordingConsentAccepted,
  ]);

  const handleJoinRequestDecision = async (requestId, status) => {
    setJoinRequestBusyId(requestId);
    setJoinRequestError("");
    try {
      setPendingJoinRequests((current) => current.filter((request) => request.request_id !== requestId));
      const payload = await respondLiveInterviewJoinRequest(roomId, requestId, {
        actor_email: joinRequestActorEmail,
        actor_identity: joinRequestActorIdentity,
        role: joinRequestManagerRole,
        status,
      });
      const remaining = getPendingRequests(payload.pending_requests || []);
      setPendingJoinRequests(remaining);
      if (!remaining.length) {
        setWaitingPanelOpen(false);
      }
      window.setTimeout(loadPendingJoinRequests, 250);
    } catch (err) {
      console.error("[LiveInterview] Failed to update join request", err);
      setJoinRequestError(getCleanJoinRequestError(err));
      loadPendingJoinRequests();
    } finally {
      setJoinRequestBusyId("");
    }
  };

  const handleCandidateLobbyAction = async () => {
    if (needsInstantGuestNameEntry && !instantGuestNameReady) {
      setTokenError("Please enter your name (at least 2 characters) before joining.");
      return;
    }
    if (requiresJoinConsent && !recordingConsentAccepted) {
      setTokenError("Please accept the recording and transcript consent before continuing.");
      return;
    }
    if (candidateWasRemoved) {
      setTokenError(REMOVED_FROM_INTERVIEW_MESSAGE);
      return;
    }
    void requestBrowserNotificationPermission();
    if (role !== "candidate") {
      if (!staffDisplayNameReady) {
        setTokenError("Please enter your name (at least 2 characters) before joining.");
        return;
      }
      void requestToken();
      return;
    }

    if (!candidateNeedsApproval) {
      candidateHoldLobbyAfterLeaveRef.current = false;
      void requestToken("", { explicitCandidateJoin: true });
      return;
    }

    if (candidateJoinApproved || candidateRequestStatus === "joined") {
      candidateHoldLobbyAfterLeaveRef.current = false;
      void requestToken("", { skipLivekitPrecheck: true, explicitCandidateJoin: true });
      return;
    }

    if (candidateJoinPending || candidateRequestBusy) {
      return;
    }

    setCandidateRequestBusy(true);
    setTokenError("");
    try {
      const payload = await requestLiveInterviewInviteJoin(inviteToken, {
        display_name: displayName,
        recording_consent_accepted: recordingConsentAccepted,
      });
      const nextRequestId = payload?.request?.request_id || "";
      const nextStatus = normalizeJoinRequestStatus(payload?.request?.status || "pending");
      setCandidateRequestId(nextRequestId);
      setCandidateRequestStatus(nextStatus);
      setCandidateWaitingNotice(getCandidateJoinStatusMessage(nextStatus));
      candidateHoldLobbyAfterLeaveRef.current = false;
    } catch (err) {
      console.error("[LiveInterview] Failed to request to join", err);
      setTokenError(
        err?.payload?.message ||
          err?.payload?.error ||
          err.message ||
          "Unable to send your join request."
      );
    } finally {
      setCandidateRequestBusy(false);
    }
  };

  const handleRemoveParticipant = async (identity) => {
    if (!identity) return;
    setParticipantRemoveBusyIdentity(identity);
    setParticipantRemoveError("");
    try {
      const payload = await removeLiveInterviewParticipant(roomId, {
        identity,
        actor_email: currentUserEmail,
        actor_identity: joinRequestActorIdentity,
        participant_identity: joinRequestActorIdentity,
        role: meetingManagerRole || role,
      });
      setRoomData((prev) => ({
        ...(prev || {}),
        ...(payload && typeof payload === "object" && payload.room != null ? { room: payload.room } : {}),
      }));
      showHostActionToast("Participant removed from the interview.");
      void loadPendingJoinRequests();
    } catch (err) {
      console.error("[LiveInterview] Failed to remove participant", err);
      setParticipantRemoveError(err.message || "Participant could not be removed.");
    } finally {
      setParticipantRemoveBusyIdentity("");
    }
  };

  const showHostActionToast = useCallback((message) => {
    if (hostToastTimerRef.current) {
      window.clearTimeout(hostToastTimerRef.current);
    }
    setHostActionToast(message);
    hostToastTimerRef.current = window.setTimeout(() => {
      setHostActionToast("");
      hostToastTimerRef.current = null;
    }, 5000);
  }, []);

  useEffect(() => {
    return () => {
      if (hostToastTimerRef.current) {
        window.clearTimeout(hostToastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (role !== "candidate" || !joined) {
      candidateRecordingLiveRef.current = recordingLive;
      return;
    }
    if (recordingLive && !candidateRecordingLiveRef.current) {
      showHostActionToast("This meeting is now being recorded.");
    }
    candidateRecordingLiveRef.current = recordingLive;
  }, [joined, recordingLive, role, showHostActionToast]);

  const executeMuteAllCandidates = async (wantMuted) => {
    if (!roomId) return false;
    setMuteAllBusy(true);
    setMuteControlsError("");
    try {
      const payload = await muteAllLiveInterviewCandidates(roomId, {
        muted: Boolean(wantMuted),
        actor_email: currentUserEmail,
        actor_identity: joinRequestActorIdentity,
        participant_identity: joinRequestActorIdentity,
        role: meetingManagerRole || role,
      });
      if (payload) {
        setRoomData((prev) => ({
          ...(prev || {}),
          ...(payload && typeof payload === "object" && payload.room != null ? { room: payload.room } : {}),
        }));
      }
      const nIdent = Number(payload?.mute?.affected_identity_count);
      const nTracks = Number(payload?.mute?.affected_track_count);
      const hasCount = Number.isFinite(nIdent) && nIdent > 0;
      if (wantMuted) {
        if (hasCount) {
          showHostActionToast("All candidates have been muted.");
        } else {
          showHostActionToast("Mute was applied. No candidate microphones were connected in LiveKit.");
        }
      } else if (hasCount || (Number.isFinite(nTracks) && nTracks > 0)) {
        showHostActionToast("Candidate microphones have been unmuted.");
      } else {
        showHostActionToast("Unmute was applied. No candidate microphones were updated in LiveKit.");
      }
      return true;
    } catch (err) {
      console.error("[LiveInterview] Failed to mute all candidates", err);
      setMuteControlsError(err.message || "Could not update candidate microphones.");
      return false;
    } finally {
      setMuteAllBusy(false);
    }
  };

  const handleMuteAllCandidatesClick = (wantMuted) => {
    if (wantMuted) {
      setMuteControlsError("");
      setMuteAllConfirmOpen(true);
      return;
    }
    void executeMuteAllCandidates(false);
  };

  const handleConfirmMuteAllCandidates = async () => {
    const ok = await executeMuteAllCandidates(true);
    if (ok) setMuteAllConfirmOpen(false);
  };

  const handleMuteCandidateMic = async (identity, currentlyMicEnabled) => {
    if (!identity || !roomId) return;
    setMuteCandidateBusyIdentity(identity);
    setMuteControlsError("");
    try {
      const payload = await muteLiveInterviewParticipantCandidate(roomId, identity, {
        muted: Boolean(currentlyMicEnabled),
        actor_email: currentUserEmail,
        actor_identity: joinRequestActorIdentity,
        participant_identity: joinRequestActorIdentity,
        role: meetingManagerRole || role,
      });
      if (payload) {
        setRoomData((prev) => ({
          ...(prev || {}),
          ...(payload && typeof payload === "object" && payload.room != null ? { room: payload.room } : {}),
        }));
      }
    } catch (err) {
      console.error("[LiveInterview] Failed to mute candidate", err);
      setMuteControlsError(err.message || "Could not update that microphone.");
    } finally {
      setMuteCandidateBusyIdentity("");
    }
  };

  const handleMuteSelectedCandidateIdentities = async (identities, wantMuted) => {
    if (!roomId || !identities?.length) return false;
    setMuteAllBusy(true);
    setMuteControlsError("");
    try {
      const payload = await muteLiveInterviewCandidates(roomId, {
        identities,
        muted: Boolean(wantMuted),
        actor_email: currentUserEmail,
        actor_identity: joinRequestActorIdentity,
        participant_identity: joinRequestActorIdentity,
        role: meetingManagerRole || role,
      });
      if (payload) {
        setRoomData((prev) => ({
          ...(prev || {}),
          ...(payload && typeof payload === "object" && payload.room != null ? { room: payload.room } : {}),
        }));
      }
      const nIdent = Number(payload?.mute?.affected_identity_count);
      const hasCount = Number.isFinite(nIdent) && nIdent > 0;
      const missing = payload?.mute?.not_connected_identities;
      const missLen = Array.isArray(missing) ? missing.length : 0;
      if (wantMuted) {
        if (hasCount) {
          showHostActionToast(
            missLen
              ? `Muted ${nIdent} candidate(s). ${missLen} not connected.`
              : `Muted ${nIdent} selected candidate(s).`
          );
        } else {
          showHostActionToast(
            missLen
              ? "No microphones updated. Some selected candidates are not connected."
              : "Mute was applied. No candidate microphones were updated in LiveKit."
          );
        }
      } else if (hasCount) {
        showHostActionToast(
          missLen ? `Unmuted ${nIdent} candidate(s). ${missLen} not connected.` : `Unmuted ${nIdent} selected candidate(s).`
        );
      } else {
        showHostActionToast(
          missLen
            ? "No microphones updated. Some selected candidates are not connected."
            : "Unmute was applied. No candidate microphones were updated in LiveKit."
        );
      }
      return true;
    } catch (err) {
      console.error("[LiveInterview] Failed to mute selected candidates", err);
      setMuteControlsError(err.message || "Could not update selected candidate microphones.");
      return false;
    } finally {
      setMuteAllBusy(false);
    }
  };

  const handleCancel = () => {
    setCancelConfirmOpen(false);
    if (meetingUiTerminated) {
      return;
    }
    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  };

  const handleCloseEndedSession = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }
    try {
      window.open("", "_self");
      window.close();
    } catch {
      // Some browsers block programmatic close for tabs opened manually.
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  }, [navigate]);

  const feedbackBookingId = String(
    liveMeetBookingId || roomData?.room?.booking_id || ""
  ).trim();
  const feedbackRoomId = String(roomId || "").trim();
  const hasFeedbackTarget = Boolean(feedbackBookingId || feedbackRoomId);
  const shouldPromptInterviewerFeedback =
    canManageInterviewFeedback && hasFeedbackTarget;

  const cancelPendingFeedbackAutoEmail = useCallback(async () => {
    if (role === "candidate" || !hasFeedbackTarget || !currentUserEmail) {
      return;
    }
    try {
      await cancelInterviewerFeedbackAutoEmail({
        bookingId: feedbackBookingId || undefined,
        roomId: feedbackRoomId || undefined,
        interviewerEmail: currentUserEmail,
        actorEmail: currentUserEmail,
        interviewerToken: interviewerTokenFromUrl || undefined,
      });
    } catch (err) {
      console.warn("[LiveInterview] feedback auto-email cancel failed", {
        message: err?.message || err,
      });
    }
  }, [
    currentUserEmail,
    feedbackBookingId,
    feedbackRoomId,
    hasFeedbackTarget,
    role,
    interviewerTokenFromUrl,
  ]);

  const queueInterviewerFeedbackAutoEmail = useCallback(
    async (reason = "leave", { force = false, keepalive = false } = {}) => {
      if (role === "candidate" || !hasFeedbackTarget || !currentUserEmail) {
        return false;
      }
      if (feedbackCompletedInMeetingRef.current) {
        return false;
      }
      if (
        !force &&
        (postMeetingFeedbackActiveRef.current ||
          sessionEndedByHostRef.current ||
          roomHasEndedRef.current)
      ) {
        return false;
      }
      const bookingId = feedbackBookingId;
      const resolvedRoomId = feedbackRoomId;
      const interviewerEmail = normalizeEmail(currentUserEmail);
      const retryKey = buildFeedbackEmailRetryKey(
        bookingId,
        resolvedRoomId,
        interviewerEmail
      );
      if (!retryKey) {
        return false;
      }
      if (!force && feedbackAutoEmailQueuedKeysRef.current.has(retryKey)) {
        return true;
      }

      const payload = {
        bookingId: bookingId || undefined,
        roomId: resolvedRoomId || undefined,
        interviewerEmail,
        actorEmail: interviewerEmail,
        interviewerToken: interviewerTokenFromUrl || undefined,
        keepalive,
      };

      try {
        await scheduleInterviewerFeedbackAutoEmail(payload);
        feedbackAutoEmailQueuedKeysRef.current.add(retryKey);
        removeFeedbackEmailRetryEntry(bookingId, resolvedRoomId, interviewerEmail);
        return true;
      } catch (err) {
        upsertFeedbackEmailRetryEntry(payload);
        console.warn("[LiveInterview] feedback auto-email schedule failed", {
          reason,
          message: err?.message || err,
        });
        return false;
      }
    },
    [
      currentUserEmail,
      feedbackBookingId,
      feedbackRoomId,
      hasFeedbackTarget,
      role,
      interviewerTokenFromUrl,
    ]
  );

  useEffect(() => {
    if (role === "candidate" || !browserOnline) {
      return undefined;
    }
    const bookingId = feedbackBookingId;
    const resolvedRoomId = feedbackRoomId;
    const interviewerEmail = normalizeEmail(currentUserEmail);
    const retryKey = buildFeedbackEmailRetryKey(
      bookingId,
      resolvedRoomId,
      interviewerEmail
    );
    if (!retryKey) {
      return undefined;
    }
    const hasPendingRetry = readFeedbackEmailRetryEntries().some(
      (entry) => String(entry?.retry_key || "").trim() === retryKey
    );
    if (!hasPendingRetry) {
      return undefined;
    }
    let cancelled = false;
    const retryTimer = window.setTimeout(() => {
      if (cancelled) return;
      void queueInterviewerFeedbackAutoEmail("retry-online", { force: true });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
    };
  }, [
    browserOnline,
    currentUserEmail,
    feedbackBookingId,
    feedbackRoomId,
    queueInterviewerFeedbackAutoEmail,
    role,
  ]);

  useEffect(() => {
    if (role === "candidate") {
      return undefined;
    }
    const sendFeedbackLinkOnAbandon = () => {
      if (feedbackCompletedInMeetingRef.current) {
        return;
      }
      if (
        !postMeetingFeedbackActiveRef.current &&
        !feedbackWizardOpenRef.current
      ) {
        return;
      }
      if (!hasFeedbackTarget || !currentUserEmail) {
        return;
      }
      const payload = {
        bookingId: feedbackBookingId || undefined,
        roomId: feedbackRoomId || undefined,
        interviewerEmail: currentUserEmail,
        actorEmail: currentUserEmail,
        interviewerToken: interviewerTokenFromUrl || undefined,
        keepalive: true,
      };
      void deferInterviewerFeedback(payload).catch(() => {
        void queueInterviewerFeedbackAutoEmail("feedback-pagehide", {
          force: true,
          keepalive: true,
        });
      });
    };
    window.addEventListener("pagehide", sendFeedbackLinkOnAbandon);
    window.addEventListener("beforeunload", sendFeedbackLinkOnAbandon);
    return () => {
      window.removeEventListener("pagehide", sendFeedbackLinkOnAbandon);
      window.removeEventListener("beforeunload", sendFeedbackLinkOnAbandon);
    };
  }, [
    currentUserEmail,
    feedbackBookingId,
    feedbackRoomId,
    hasFeedbackTarget,
    interviewerTokenFromUrl,
    queueInterviewerFeedbackAutoEmail,
    role,
  ]);

  const performLeaveMeetingOnly = useCallback(async () => {
    const identity = (tokenState?.identity || "").trim();
    if (!roomId || !identity) {
      void queueInterviewerFeedbackAutoEmail("leave-missing-identity", { force: true });
      return;
    }
    console.log("[LIVEKIT_LEAVE] leave-only confirmed", { role, roomId, identity });
    manualLeaveRequestedRef.current = true;
    setLiveSessionState("lobby");
    setMeetingError("");
    setTokenError("");
    if (role === "candidate") {
      candidateHoldLobbyAfterLeaveRef.current = true;
      setCandidateLivekitSdkEnabled(false);
      setCandidateRequestStatus("left");
      setCandidateWaitingNotice(getCandidateJoinStatusMessage("left"));
    }
    joinedRef.current = false;
    setJoined(false);
    setTokenState(null);
    setActivePanel("");
    setLeaveExitModalOpen(false);
    const feedbackEmailPromise = queueInterviewerFeedbackAutoEmail("leave");
    try {
      await leaveLiveInterviewParticipant(roomId, {
        role: meetingManagerRole || role,
        actor_identity: identity,
        participant_identity: identity,
        actor_email: currentUserEmail || undefined,
        invite_token: inviteToken || undefined,
        booking_id: liveMeetBookingId || undefined,
      });
      console.info("[LIVEKIT_LEAVE] participant-left synced", { roomId, identity, role });
    } catch (apiError) {
      console.warn("[LIVEKIT_LEAVE] backend participant-left failed, disconnecting locally", {
        roomId,
        identity,
        role,
        error: apiError?.message || apiError,
      });
    } finally {
      await feedbackEmailPromise;
    }
  }, [
    currentUserEmail,
    inviteToken,
    liveMeetBookingId,
    meetingManagerRole,
    queueInterviewerFeedbackAutoEmail,
    role,
    roomId,
    tokenState?.identity,
  ]);

  const handleLeaveButtonClick = useCallback((event) => {
    event?.preventDefault?.();
    setLeaveExitModalOpen(true);
    return false;
  }, []);

  const handleConfirmLeaveOnly = useCallback(async () => {
    setLeavingMeeting(true);
    setMeetingError("");
    try {
      await performLeaveMeetingOnly();
    } catch (err) {
      setMeetingError(err?.message || "Could not leave the meeting.");
    } finally {
      setLeavingMeeting(false);
    }
  }, [performLeaveMeetingOnly]);

  const handleCandidatePresenceChange = useCallback((present) => {
    setCandidateInRoom(Boolean(present));
  }, []);

  const finalizePostMeetingFeedbackFlow = useCallback(() => {
    postMeetingFeedbackActiveRef.current = false;
    setPostMeetingFeedbackActive(false);
    joinedRef.current = false;
    setJoined(false);
    setTokenState(null);
    setActivePanel("");
    setLiveSessionState("ended");
  }, []);

  const performEndMeetingForAll = useCallback(async ({ preserveFeedbackFlow = false } = {}) => {
    if (!roomId || !canInterviewTeamManageMeeting) return false;
    setEndingMeeting(true);
    setMeetingError("");
    manualLeaveRequestedRef.current = true;
    if (preserveFeedbackFlow) {
      postMeetingFeedbackActiveRef.current = true;
      setPostMeetingFeedbackActive(true);
    }
    // Mark the session terminal before LiveKit disconnect handlers run so they
    // do not queue grace-period feedback emails while close is in flight.
    sessionEndedByHostRef.current = true;
    setSessionEndedByHost(true);
    candidateHoldLobbyAfterLeaveRef.current = true;
    try {
      await closeLiveInterviewRoom(roomId, {
        actor_email: currentUserEmail,
        actor_identity: joinRequestActorIdentity,
        role: meetingManagerRole || role,
        booking_id: liveMeetBookingId,
      });
      if (preserveFeedbackFlow) {
        await cancelPendingFeedbackAutoEmail();
      }
      setHostActionToast("Meeting ended.");
      setLiveSessionState("ended");
      setLeaveExitModalOpen(false);
      if (preserveFeedbackFlow) {
        setFeedbackChoiceOpen(true);
      } else {
        joinedRef.current = false;
        setJoined(false);
        setTokenState(null);
        setActivePanel("");
        setFeedbackChoiceOpen(false);
        setFeedbackWizardOpen(false);
      }
      void loadRoom({ silent: true });
      return true;
    } catch (err) {
      manualLeaveRequestedRef.current = false;
      sessionEndedByHostRef.current = false;
      setSessionEndedByHost(false);
      candidateHoldLobbyAfterLeaveRef.current = false;
      if (preserveFeedbackFlow) {
        postMeetingFeedbackActiveRef.current = false;
        setPostMeetingFeedbackActive(false);
      }
      setMeetingError(err?.message || "Failed to end meeting.");
      return false;
    } finally {
      setEndingMeeting(false);
    }
  }, [
    roomId,
    canInterviewTeamManageMeeting,
    currentUserEmail,
    joinRequestActorIdentity,
    meetingManagerRole,
    role,
    liveMeetBookingId,
    cancelPendingFeedbackAutoEmail,
    loadRoom,
  ]);

  const handleConfirmEndMeeting = useCallback(async () => {
    if (!roomId || !canInterviewTeamManageMeeting) return;
    setFeedbackFlowError("");
    setLeaveExitModalOpen(false);
    await performEndMeetingForAll({
      preserveFeedbackFlow: shouldPromptInterviewerFeedback,
    });
  }, [
    roomId,
    canInterviewTeamManageMeeting,
    shouldPromptInterviewerFeedback,
    performEndMeetingForAll,
  ]);

  const handleFeedbackGiveNow = useCallback(async () => {
    if (!hasFeedbackTarget) return;
    postMeetingFeedbackActiveRef.current = true;
    setFeedbackFlowBusy(true);
    setFeedbackFlowError("");
    try {
      const data = await prepareInterviewerFeedback({
        bookingId: feedbackBookingId || undefined,
        roomId: feedbackRoomId || undefined,
        interviewerEmail: currentUserEmail,
        actorEmail: currentUserEmail,
        interviewerToken: interviewerTokenFromUrl || undefined,
      });
      const token = String(data?.access_token || "").trim();
      if (!token) {
        throw new Error("Feedback session could not be started.");
      }
      setFeedbackAccessToken(token);
      setFeedbackChoiceOpen(false);
      setFeedbackWizardOpen(true);
      void queueInterviewerFeedbackAutoEmail("in-meeting-recovery", {
        force: true,
      });
    } catch (err) {
      const message = err?.message || "Could not start feedback session.";
      if (message.toLowerCase().includes("already been submitted")) {
        setFeedbackChoiceOpen(false);
        finalizePostMeetingFeedbackFlow();
        return;
      }
      setFeedbackFlowError(message);
    } finally {
      setFeedbackFlowBusy(false);
    }
  }, [
    currentUserEmail,
    feedbackBookingId,
    feedbackRoomId,
    hasFeedbackTarget,
    interviewerTokenFromUrl,
    queueInterviewerFeedbackAutoEmail,
    finalizePostMeetingFeedbackFlow,
  ]);

  const handleFeedbackGiveLater = useCallback(async () => {
    if (!hasFeedbackTarget) return;
    setFeedbackFlowBusy(true);
    setFeedbackFlowError("");
    try {
      const data = await deferInterviewerFeedback({
        bookingId: feedbackBookingId || undefined,
        roomId: feedbackRoomId || undefined,
        interviewerEmail: currentUserEmail,
        actorEmail: currentUserEmail,
        interviewerToken: interviewerTokenFromUrl || undefined,
      });
      removeFeedbackEmailRetryEntry(
        feedbackBookingId,
        feedbackRoomId,
        currentUserEmail
      );
      const validityLabel = data?.token_ttl_label || FEEDBACK_LINK_VALIDITY_LABEL;
      setHostActionToast(
        `Feedback link sent to your email. It stays valid for ${validityLabel}.`
      );
      setFeedbackChoiceOpen(false);
      finalizePostMeetingFeedbackFlow();
    } catch (err) {
      setFeedbackFlowError(err?.message || "Could not send feedback link.");
    } finally {
      setFeedbackFlowBusy(false);
    }
  }, [
    currentUserEmail,
    feedbackBookingId,
    feedbackRoomId,
    hasFeedbackTarget,
    interviewerTokenFromUrl,
    finalizePostMeetingFeedbackFlow,
  ]);

  const handleFeedbackSubmitted = useCallback(() => {
    feedbackCompletedInMeetingRef.current = true;
    postMeetingFeedbackActiveRef.current = false;
    removeFeedbackEmailRetryEntry(
      feedbackBookingId,
      feedbackRoomId,
      currentUserEmail
    );
    void cancelPendingFeedbackAutoEmail();
    setFeedbackWizardOpen(false);
    setFeedbackChoiceOpen(false);
    setHostActionToast("Feedback submitted.");
    finalizePostMeetingFeedbackFlow();
  }, [
    cancelPendingFeedbackAutoEmail,
    currentUserEmail,
    feedbackBookingId,
    feedbackRoomId,
    finalizePostMeetingFeedbackFlow,
  ]);

  const canShowPostMeetingFeedback =
    role !== "candidate" && (feedbackChoiceOpen || feedbackWizardOpen);

  const handleDisconnected = (reason) => {
    const wasManualLeave = manualLeaveRequestedRef.current;
    manualLeaveRequestedRef.current = false;
    console.info("[LiveInterview] Disconnected from LiveKit room", { reason });

    // The host/co-host ended the meeting → LiveKit deletes the room and pushes
    // a ROOM_DELETED / SERVER_SHUTDOWN disconnect to every client. Treat that
    // (or a room we already know is ended via polling) as a terminal state:
    // show "the host ended the interview" with NO rejoin option, since the
    // room no longer exists server-side.
    const endedByHost =
      sessionEndedByHostRef.current ||
      roomHasEndedRef.current ||
      (!wasManualLeave &&
        (reason === DisconnectReason.ROOM_DELETED ||
          reason === DisconnectReason.SERVER_SHUTDOWN));
    const holdMeetingUiForFeedback =
      role !== "candidate" && postMeetingFeedbackActiveRef.current;

    if (endedByHost) {
      sessionEndedByHostRef.current = true;
      setSessionEndedByHost(true);
      setLiveSessionState("ended");
      // Block any auto-rejoin path and clear the reconnect-style notice.
      candidateHoldLobbyAfterLeaveRef.current = true;
      setCandidateWaitingNotice("");
      if (holdMeetingUiForFeedback) {
        if (!feedbackCompletedInMeetingRef.current) {
          void queueInterviewerFeedbackAutoEmail("feedback-disconnect", {
            force: true,
          });
        }
        return;
      }
      joinedRef.current = false;
      setActivePanel("");
      setJoined(false);
      setTokenState(null);
      if (role === "candidate") {
        setCandidateLivekitSdkEnabled(false);
      }
      return;
    }

    joinedRef.current = false;
    setActivePanel("");
    setJoined(false);
    setTokenState(null);
    if (role === "candidate") {
      setCandidateLivekitSdkEnabled(false);
    }

    setLiveSessionState(wasManualLeave ? "lobby" : "disconnected");
    if (role === "candidate" && wasManualLeave) {
      candidateHoldLobbyAfterLeaveRef.current = true;
      setCandidateRequestStatus("left");
      setCandidateWaitingNotice(getCandidateJoinStatusMessage("left"));
      setTokenError("");
      setPreJoinCameraOn(false);
      setPreJoinMicOn(false);
      return;
    }
    if (role === "candidate" && !wasManualLeave) {
      candidateHoldLobbyAfterLeaveRef.current = true;
      setCandidateWaitingNotice((prev) =>
        prev && prev.toLowerCase().includes("left")
          ? prev
          : getLobbyRejoinCopy({ canRejoin: true, role: "candidate" })?.notice ||
            "You disconnected from the meeting. Click Rejoin Meeting when you are ready."
      );
      return;
    }
    if (
      role !== "candidate" &&
      !wasManualLeave &&
      !feedbackCompletedInMeetingRef.current &&
      !sessionEndedByHostRef.current &&
      !roomHasEndedRef.current &&
      !postMeetingFeedbackActiveRef.current
    ) {
      void queueInterviewerFeedbackAutoEmail("disconnect", { force: true });
    }
  };

  const handleLiveKitConnected = () => {
    setMeetingError("");
    setLiveSessionState("connected");
    setHasJoinedMeetingOnce(true);
    setMeetingStartedAtMs(Date.now());
    const safeTranscriptRoomId = String(transcriptRoomId || "").trim();
    if (
      transcriptPanelAvailable &&
      safeTranscriptRoomId &&
      autoOpenedTranscriptRoomRef.current !== safeTranscriptRoomId
    ) {
      autoOpenedTranscriptRoomRef.current = safeTranscriptRoomId;
      setActivePanel("transcript");
    }
    console.info("[LiveInterview] Connected to LiveKit room", {
      roomId,
      serverUrl: tokenState?.server_url,
    });
  };

  // Tick once per second when we have a meeting start time so the elapsed
  // clock in the top bar updates without coupling to LiveKit events.
  useEffect(() => {
    if (meetingStartedAtMs == null) return undefined;
    const id = window.setInterval(() => setMeetingTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [meetingStartedAtMs]);

  // Clear timer when we leave / disconnect.
  useEffect(() => {
    if (!joined) {
      setMeetingStartedAtMs(null);
      setMeetingTick(0);
    }
  }, [joined]);

  // Stable LiveKit prop references — recomputing these inline on every
  // render makes <LiveKitRoom> think the audio/video config changed and try
  // to reconnect, which floods the console with "already connected to room"
  // warnings (especially after the meeting clock starts ticking every 1s).
  const livekitAudioOptions = useMemo(() => {
    if (!preJoinMicOn) return false;
    const options = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    const deviceId = String(preJoinAudioDeviceId || "").trim();
    if (deviceId) {
      options.deviceId = deviceId;
    }
    return options;
  }, [preJoinMicOn, preJoinAudioDeviceId]);
  const livekitVideoOptions = useMemo(() => {
    if (!preJoinCameraOn) return false;
    const deviceId = String(preJoinVideoDeviceId || "").trim();
    return deviceId ? { deviceId } : true;
  }, [preJoinCameraOn, preJoinVideoDeviceId]);
  const livekitConnect = useMemo(
    () => role !== "candidate" || candidateLivekitSdkEnabled,
    [role, candidateLivekitSdkEnabled]
  );

  const meetingDurationLabel = useMemo(() => {
    if (meetingStartedAtMs == null) return "";
    void meetingTick;
    const totalSeconds = Math.max(0, Math.floor((Date.now() - meetingStartedAtMs) / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [meetingStartedAtMs, meetingTick]);

  const handleLiveKitError = (err) => {
    console.error("[LiveInterview] LiveKit connection error", err);
    setLiveSessionState("error");
    setMeetingError(err?.message || "LiveKit connection failed. Check server URL, token, and network access.");
  };

  const handleMediaDeviceFailure = (failure, kind) => {
    console.error("[LiveInterview] Media device permission/capture failed", { failure, kind });
    const deviceName = kind === "videoinput" ? "camera" : kind === "audioinput" ? "microphone" : "media device";
    setLiveSessionState("error");
    setMeetingError(`Could not start ${deviceName}. Please allow browser permissions and confirm the device is not used by another app.`);
  };
  const handleRecordingToggle = async () => {
    if (recordingProcessing) return;
    setRecordingBusy(true);
    setRecordingError("");
    try {
      const payload = recordingLive
        ? await stopLiveInterviewRecording(roomId, { actor_email: currentUserEmail, role })
        : await startLiveInterviewRecording(roomId, { actor_email: currentUserEmail, role });
      setRecordingStatus(
        normalizeRecordingStatus(
          payload.room?.recording_status || (recordingLive ? "idle" : "recording")
        )
      );
      if (payload.room) {
        setRoomData((prev) => ({ ...(prev || {}), room: payload.room }));
      }
      await loadRoom();
    } catch (err) {
      setRecordingError(err.message || "Recording could not be updated.");
    } finally {
      setRecordingBusy(false);
    }
  };

  const handleRecordingJoinPromptNo = useCallback(() => {
    recordingJoinPromptDismissedRef.current = true;
    setRecordingJoinPromptVisible(false);
  }, []);

  const handleRecordingJoinPromptYes = useCallback(() => {
    recordingJoinPromptDismissedRef.current = true;
    setRecordingJoinPromptVisible(false);
    void handleRecordingToggle();
  }, [handleRecordingToggle]);

  const handleRecordingDownload = async (recordingId) => {
    if (!recordingId || !roomId) return;
    setRecordingDownloadBusyId(recordingId);
    setRecordingError("");
    try {
      const payload = await getLiveInterviewRecordingDownloadUrl(roomId, recordingId, {
        actorEmail: currentUserEmail,
        role,
      });
      const url = payload?.download?.url;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setRecordingError(err.message || "Download link could not be created.");
    } finally {
      setRecordingDownloadBusyId("");
    }
  };

  const handleTranscriptPdfOpen = async () => {
    if (!roomId) return;
    setTranscriptPdfBusy(true);
    setRecordingError("");
    try {
      const payload = await getLiveInterviewTranscriptPdfUrl(roomId, {
        actorEmail: currentUserEmail,
        role,
      });
      const url = String(payload?.pdf_url || payload?.transcript_pdf?.url || "").trim();
      if (!url) {
        throw new Error(payload?.message || "Transcript PDF link is not ready yet.");
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setRecordingError(err.message || "Transcript PDF could not be opened.");
    } finally {
      setTranscriptPdfBusy(false);
    }
  };

  const handleTranscriptEnabledChange = async (nextEnabled) => {
    setTranscriptBusy(true);
    setTranscriptError("");
    try {
      const payload = await updateLiveInterviewRoomSettings(roomId, {
        actor_email: currentUserEmail,
        role,
        transcript_enabled: nextEnabled,
      });
      setRoomData(payload);
    } catch (err) {
      setTranscriptError(err.message || "Transcript setting could not be updated.");
    } finally {
      setTranscriptBusy(false);
    }
  };

  const handleServerTranscriptDisabled = useCallback(() => {
    setRoomData((current) => {
      if (!current?.room) return current;
      return {
        ...current,
        room: {
          ...current.room,
          transcript_enabled: false,
        },
      };
    });
  }, []);

  const handleBrowserTranscriptStatus = useCallback((nextStatus) => {
    setBrowserTranscriptStatus(nextStatus || null);
  }, []);

  const handleTranscriptSegmentSaved = useCallback(
    (nextMutation) => {
      const safeRoomId = String(transcriptRoomId || "").trim();
      const mutationRoomId = String(nextMutation?.roomId || "").trim();
      const segment = nextMutation?.segment;
      if (
        !transcriptPanelAvailable ||
        !safeRoomId ||
        mutationRoomId !== safeRoomId ||
        !segment?.segment_id
      ) {
        return;
      }
      const eventId = transcriptMutationEventIdRef.current + 1;
      transcriptMutationEventIdRef.current = eventId;
      const sequence = Number(nextMutation?.sequence);
      setTranscriptMutation({
        roomId: safeRoomId,
        segment,
        sequence: Number.isFinite(sequence) && sequence > 0 ? sequence : eventId,
        eventId,
      });
    },
    [transcriptPanelAvailable, transcriptRoomId]
  );

  const togglePanel = (panelName) => {
    if (panelName === "transcript" && !transcriptPanelAvailable) {
      return;
    }
    if (panelName === "recordings" && !canManageRecording) {
      return;
    }
    setActivePanel((currentPanel) => (currentPanel === panelName ? "" : panelName));
  };

  useEffect(() => {
    if (!transcriptPanelAvailable && activePanel === "transcript") {
      setActivePanel("");
    }
  }, [activePanel, transcriptPanelAvailable]);

  const handleFullscreenToggle = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      await meetingPanelRef.current?.requestFullscreen?.();
    } catch (err) {
      console.error("[LiveInterview] Fullscreen toggle failed", err);
      setMeetingError("Fullscreen could not be opened. Please try the browser fullscreen control.");
    }
  };

  return (
    <div
      className={`live-interview-page ${joined ? "is-meeting" : "is-lobby"}${role !== "candidate" ? " is-interviewer-team" : ""}${isVerifiedInterviewerLobby ? " is-verified-interviewer-lobby" : ""}`}
    >
      <main className="live-interview-shell">
        {!joined && !meetingUiTerminated ? (
          <header className="li-lobby-topbar">
            <div className="li-lobby-topbar-brand">
              <img
                className="li-lobby-brand-logo"
                src="/searchjoblogo.svg"
                alt="Search Job"
              />
            </div>
            <div className="li-lobby-topbar-actions">
              <button
                type="button"
                className="li-lobby-close"
                onClick={() => setCancelConfirmOpen(true)}
                aria-label="Leave the interview room"
                title="Leave interview room"
              >
                <X size={19} aria-hidden />
              </button>
            </div>
          </header>
        ) : null}

        {error && !joined ? (
          <div className="li-lobby-banner is-error" role="alert">
            We could not load this interview room. Refresh the page or contact the hiring team.
          </div>
        ) : null}

        {!joined && cancelConfirmOpen ? (
          <div
            className="li-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="live-interview-cancel-title"
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setCancelConfirmOpen(false);
              }
            }}
          >
            <div
              className="li-modal-card"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="li-modal-icon" aria-hidden>
                <LogOut size={20} />
              </div>
              <h3 className="li-modal-title" id="live-interview-cancel-title">
                Leave the interview room?
              </h3>
              <p className="li-modal-body">
                You can return to this interview using the same link.
              </p>
              <div className="li-modal-actions">
                <button
                  type="button"
                  className="li-modal-btn li-modal-btn-secondary"
                  onClick={() => setCancelConfirmOpen(false)}
                >
                  Stay
                </button>
                <button
                  type="button"
                  className="li-modal-btn li-modal-btn-danger"
                  onClick={handleCancel}
                >
                  <LogOut size={15} aria-hidden /> Leave room
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <section className={`live-interview-grid ${joined ? "joined" : ""}`}>
          <div className="live-interview-main-column">
            {showLiveKitMeeting ? (
              <div
                className={`live-interview-panel live-interview-livekit-panel ${isFullscreen ? "live-interview-fullscreen-active" : ""}`}
                ref={meetingPanelRef}
              >
                <div className="live-interview-meeting-wrap" ref={meetingWrapRef}>
                  {hostActionToast ? (
                    <div
                      className="live-interview-notice live-interview-inline-toast live-interview-host-action-toast"
                      role="status"
                    >
                      {hostActionToast}
                    </div>
                  ) : null}
                  {recordingJoinPromptVisible ? (
                    <div
                      className="li-recording-join-prompt"
                      role="dialog"
                      aria-labelledby="li-recording-join-prompt-title"
                      aria-live="polite"
                    >
                      <p id="li-recording-join-prompt-title" className="li-recording-join-prompt-text">
                        Do you want to record?
                      </p>
                      <div className="li-recording-join-prompt-actions">
                        <button
                          type="button"
                          className="li-recording-join-prompt-btn is-secondary"
                          onClick={handleRecordingJoinPromptNo}
                        >
                          No
                        </button>
                        <button
                          type="button"
                          className="li-recording-join-prompt-btn is-primary"
                          onClick={handleRecordingJoinPromptYes}
                          disabled={recordingBusy || recordingProcessing}
                        >
                          Yes
                        </button>
                      </div>
                    </div>
                  ) : null}
                  <div className="live-interview-meeting-body">
                    <LiveKitRoom
                      className={`live-interview-livekit-root ${activePanel ? "with-drawer" : ""}`}
                      token={tokenState.token}
                      serverUrl={tokenState.server_url}
                      connect={livekitConnect}
                      audio={livekitAudioOptions}
                      video={livekitVideoOptions}
                      onConnected={handleLiveKitConnected}
                      onDisconnected={handleDisconnected}
                      onError={handleLiveKitError}
                      onMediaDeviceFailure={handleMediaDeviceFailure}
                      data-lk-theme="default"
                    >
                      <LiveInterviewRoomContent
                        activePanel={activePanel}
                        onClosePanel={() => setActivePanel("")}
                        onOpenPanel={(panelName) => setActivePanel(panelName)}
                        onUnreadChatCountChange={setChatUnreadCount}
                        meetingWrapRef={meetingWrapRef}
                        displayName={displayName}
                        meetingError={meetingError}
                        audioOutputDeviceId={audioOutputDeviceId}
                        onLeave={handleLeaveButtonClick}
                        leaveDisabled={endingMeeting || leavingMeeting}
                        leaveLabel={
                          canInterviewTeamManageMeeting
                            ? "Leave or end meeting"
                            : "Leave meeting"
                        }
                        SettingsComponent={LiveInterviewSettingsMenu}
                        meetingMenuProps={{
                          activePanel,
                          onOpenPanel: (panelName) => setActivePanel(panelName || ""),
                          transcriptPanelAvailable,
                          canManageRecording,
                          canInviteParticipants: canInterviewTeamManageMeeting,
                          isCandidate: role === "candidate",
                          pendingJoinRequestsCount: pendingJoinRequests.length,
                          chatUnreadCount,
                          recordingLive,
                          recordingDurationLabel: recordingLive ? recordingDurationLabel : "",
                          meetingDurationLabel,
                          isFullscreen,
                          onToggleFullscreen: handleFullscreenToggle,
                        }}
                        joinRequestState={{
                          requests: pendingJoinRequests,
                          busyRequestId: joinRequestBusyId,
                          error: joinRequestError,
                          isOpen: waitingPanelOpen,
                          onClose: () => setWaitingPanelOpen(false),
                          onRespond: handleJoinRequestDecision,
                        }}
                        chatPersistProps={{
                          roomId: transcriptRoomId,
                          actorEmail: currentUserEmail,
                          actorIdentity: joinRequestActorIdentity,
                          actorRole: meetingManagerRole || role,
                          inviteToken,
                          displayName,
                        }}
                        transcriptProps={{
                          roomId: transcriptRoomId,
                          bookingId: liveMeetBookingId,
                          speakerName: displayName,
                          speakerRole: role,
                          actorEmail: currentUserEmail,
                          actorIdentity: joinRequestActorIdentity,
                          actorRole: meetingManagerRole || role,
                          inviteToken,
                          interviewerToken: role === "candidate" ? "" : interviewerTokenFromUrl,
                          readOnly: false,
                          transcriptEnabled,
                          canManageTranscript: canInterviewTeamManageMeeting,
                          onTranscriptEnabledChange: handleTranscriptEnabledChange,
                          onServerTranscriptDisabled: handleServerTranscriptDisabled,
                          transcriptSettingBusy: transcriptBusy,
                          transcriptSettingError: transcriptError,
                          transcriptPdfEligible,
                          transcriptPdfAvailable,
                          transcriptExportStatus: roomData?.room?.post_meeting_transcript_status || "",
                          backendTranscriptDebug: roomData?.transcript_debug || null,
                          browserCaptureStatus: browserTranscriptStatus,
                          onStatusChange: handleBrowserTranscriptStatus,
                          onTranscriptSegmentSaved: handleTranscriptSegmentSaved,
                          transcriptMutation,
                          applicationContextProps:
                            role !== "candidate"
                              ? {
                                  roomId: transcriptRoomId,
                                  bookingId: liveMeetBookingId,
                                  actorEmail: currentUserEmail,
                                  actorIdentity: joinRequestActorIdentity,
                                  actorRole: meetingManagerRole || role,
                                  interviewerToken: interviewerTokenFromUrl,
                                }
                              : null,
                          hideHeader: true,
                        }}
                        participantRosterProps={{
                          canManageParticipants: canInterviewTeamManageMeeting,
                          canManageCandidateInvites,
                          canMuteCandidates: canInterviewTeamManageMeeting,
                          instantOpenJoin: isInstantOpenJoin,
                          roomId,
                          actorEmail: currentUserEmail,
                          actorIdentity: joinRequestActorIdentity,
                          role: meetingManagerRole || role,
                          operationalHostEmail: operationalHostEmail || "",
                          candidateOptions,
                          joinRequestState: canManageJoinRequests
                            ? {
                                requests: pendingJoinRequests,
                                busyRequestId: joinRequestBusyId,
                                error: joinRequestError,
                                onRespond: handleJoinRequestDecision,
                              }
                            : null,
                          onRoomDataUpdate: setRoomData,
                          onRemoveParticipant: handleRemoveParticipant,
                          onMuteAllCandidates: handleMuteAllCandidatesClick,
                          onMuteCandidate: handleMuteCandidateMic,
                          onMuteSelectedIdentities: handleMuteSelectedCandidateIdentities,
                          removeBusyIdentity: participantRemoveBusyIdentity,
                          removeError: participantRemoveError,
                          muteAllBusy,
                          muteCandidateBusyIdentity,
                          muteControlsError,
                          onHostActionToast: showHostActionToast,
                          onMuteControlErrorChange: setMuteControlsError,
                        }}
                        recordingsLibraryProps={
                          canManageRecording
                            ? {
                                retentionDays,
                                recordings: roomData?.recordings || [],
                                recordingLive,
                                recordingProcessing,
                                recordingDurationLabel,
                                onPlayOrDownload: handleRecordingDownload,
                                onViewTranscriptPdf: handleTranscriptPdfOpen,
                                recordingDownloadBusyId,
                                transcriptPdfEligible,
                                transcriptExportStatus: roomData?.room?.post_meeting_transcript_status || "",
                                transcriptPdfAvailable,
                                transcriptPdfBusy,
                                recordingError,
                                canManageRecording,
                                recordingBusy,
                                onToggleRecording: handleRecordingToggle,
                              }
                            : null
                        }
                        browserOnline={browserOnline}
                      />
                      <InterviewerCandidatePresenceWatcher
                        onPresenceChange={handleCandidatePresenceChange}
                      />
                    </LiveKitRoom>
                    {muteAllConfirmOpen ? (
                      <div
                        className="li-modal-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="live-interview-mute-all-title"
                        onClick={(event) => {
                          if (!muteAllBusy && event.target === event.currentTarget) {
                            setMuteAllConfirmOpen(false);
                          }
                        }}
                      >
                        <div
                          className="li-modal-card"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <div className="li-modal-icon is-warning" aria-hidden>
                            <MicOff size={20} />
                          </div>
                          <h3
                            className="li-modal-title"
                            id="live-interview-mute-all-title"
                          >
                            Mute every candidate?
                          </h3>
                          <p className="li-modal-body">
                            All candidates currently in the meeting will be muted. They can
                            unmute themselves at any time unless you remove them.
                          </p>
                          {muteControlsError ? (
                            <div className="li-modal-error">{muteControlsError}</div>
                          ) : null}
                          <div className="li-modal-actions">
                            <button
                              type="button"
                              className="li-modal-btn li-modal-btn-secondary"
                              onClick={() => setMuteAllConfirmOpen(false)}
                              disabled={muteAllBusy}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="li-modal-btn li-modal-btn-primary"
                              onClick={handleConfirmMuteAllCandidates}
                              disabled={muteAllBusy}
                            >
                              <MicOff size={15} aria-hidden />
                              {muteAllBusy ? "Muting…" : "Mute all"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {leaveExitModalOpen ? (
                      <div
                        className="li-modal-overlay li-leave-modal-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="live-interview-leave-exit-title"
                        onClick={(event) => {
                          if (!endingMeeting && !leavingMeeting && event.target === event.currentTarget) {
                            setLeaveExitModalOpen(false);
                          }
                        }}
                      >
                        <div
                          className="li-leave-modal-card"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="li-leave-modal-close"
                            aria-label="Close"
                            title="Close"
                            disabled={endingMeeting || leavingMeeting}
                            onClick={() => setLeaveExitModalOpen(false)}
                          >
                            <X size={18} aria-hidden />
                          </button>
                          <h3
                            className="li-leave-modal-title"
                            id="live-interview-leave-exit-title"
                          >
                            Leave meeting
                          </h3>
                          <p className="li-leave-modal-body">
                            Are you sure you want to leave this meeting?
                          </p>
                          {canInterviewTeamManageMeeting ? (
                            <p className="li-leave-modal-hint">
                              You can leave the meeting, or end it for everyone.
                            </p>
                          ) : null}
                          {meetingError ? (
                            <div className="li-modal-error">{meetingError}</div>
                          ) : null}
                          <div className="li-leave-modal-actions">
                            <button
                              type="button"
                              className="li-leave-modal-btn li-leave-modal-btn-leave"
                              onClick={handleConfirmLeaveOnly}
                              disabled={endingMeeting || leavingMeeting}
                            >
                              {leavingMeeting ? "Leaving…" : "Leave meeting"}
                            </button>
                            {canInterviewTeamManageMeeting ? (
                              <button
                                type="button"
                                className="li-leave-modal-btn li-leave-modal-btn-end"
                                onClick={handleConfirmEndMeeting}
                                disabled={endingMeeting || leavingMeeting}
                              >
                                {endingMeeting ? "Ending…" : "End meeting"}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ) : null}

                  </div>
                </div>
              </div>
            ) : meetingUiTerminated ? (
              /* Terminal state: host/co-host ended the interview for everyone.
                 No rejoin — the room is gone server-side. */
              <div className="li-lobby-grid li-lobby-ended">
                <div className="li-ended-card" role="status" aria-live="polite">
                  <button
                    type="button"
                    className="li-ended-close"
                    aria-label="Close window"
                    onClick={handleCloseEndedSession}
                  >
                    <X size={18} />
                  </button>
                  <div className="li-ended-icon" aria-hidden>
                    <PhoneOff size={26} />
                  </div>
                  <h2 className="li-ended-title">The interview has ended</h2>
                  <p className="li-ended-sub">
                    {role === "candidate"
                      ? "The host ended the interview. Thank you for your time — you can close this window."
                      : canShowPostMeetingFeedback
                        ? "This interview room has been closed. Share your feedback below when you are ready."
                        : "This interview room has been closed. You can close this window."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="li-lobby-grid">
                {/* Left column: video preview */}
                <div className="li-lobby-stage">
                  <PreJoin
                    cameraOn={preJoinCameraOn}
                    micOn={preJoinMicOn}
                    onCameraChange={setPreJoinCameraOn}
                    onMicChange={setPreJoinMicOn}
                    speakerDeviceId={audioOutputDeviceId}
                    audioDeviceId={preJoinAudioDeviceId}
                    videoDeviceId={preJoinVideoDeviceId}
                    onSpeakerChange={(deviceId) => {
                      const nextId = String(deviceId || "").trim();
                      setAudioOutputDeviceId(nextId);
                      persistAudioOutputDeviceId(nextId);
                    }}
                    onAudioDeviceChange={(deviceId) => {
                      setPreJoinAudioDeviceId(String(deviceId || "").trim());
                    }}
                    onVideoDeviceChange={(deviceId) => {
                      setPreJoinVideoDeviceId(String(deviceId || "").trim());
                    }}
                    displayName={displayName}
                  />
                  <div className="li-lobby-preview-note" role="status">
                    <ShieldCheck size={17} aria-hidden />
                    <span>Your preview is private until you join.</span>
                  </div>
                </div>

                {/* Right column: action card */}
                <aside className="li-lobby-aside">
                  {/* Main action card */}
                  <div className="li-lobby-card">
                    <div className="li-lobby-card-heading">
                      <div>
                        <h2 className="li-lobby-card-title">
                          {candidateInWaitingRoom
                            ? "Waiting for the interviewer to admit you."
                            : candidateAdmittedAutoJoin
                              ? "Joining the interview"
                              : lobbyRejoinCopy
                                ? lobbyRejoinCopy.title
                                : "Ready to join?"}
                        </h2>
                        <p className="li-lobby-card-sub">
                          {candidateInWaitingRoom
                            ? "You'll join automatically once admitted. You can still check your camera, microphone, and speakers."
                            : candidateAdmittedAutoJoin
                              ? "No extra confirmation is needed. Your selected camera, microphone, and speakers will be used."
                              : lobbyRejoinCopy
                                ? lobbyRejoinCopy.subtitle
                                : role === "candidate" && candidateNeedsApproval
                                  ? "Check your camera, microphone, and speakers before requesting to join."
                                  : "Check your name and devices before entering the interview."}
                        </p>
                      </div>
                    </div>

                    {role === "candidate" ? (
                      needsInstantGuestNameEntry ? (
                        <div className="live-interview-field live-interview-invite-name-field li-lobby-name-field">
                          <label htmlFor="instant-room-guest-name">Your name</label>
                          <input
                            id="instant-room-guest-name"
                            type="text"
                            className="live-interview-input"
                            placeholder="Enter your full name"
                            value={instantGuestName}
                            onChange={(event) => {
                              setInstantGuestName(event.target.value);
                              setTokenError("");
                            }}
                            autoComplete="name"
                            maxLength={80}
                          />
                        </div>
                      ) : (
                        <div className="li-lobby-identity">
                          <div className="li-lobby-identity-avatar" aria-hidden>
                            {String(displayName || "").trim().charAt(0).toUpperCase() || (
                              <UserRound size={18} strokeWidth={1.75} />
                            )}
                          </div>
                          <div className="li-lobby-identity-text">
                            <strong>{displayName}</strong>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="live-interview-field live-interview-invite-name-field li-lobby-name-field">
                        <label htmlFor="staff-room-display-name">
                          Your name <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="staff-room-display-name"
                          type="text"
                          className="live-interview-input"
                          placeholder="Enter your full name"
                          value={staffDisplayName}
                          onChange={(event) => {
                            setStaffDisplayName(event.target.value);
                            setTokenError("");
                          }}
                          autoComplete="name"
                          maxLength={80}
                          aria-invalid={Boolean(staffDisplayName) && !staffDisplayNameReady}
                        />
                      </div>
                    )}

                    {inMeetingLobbyParticipants.length ? (
                      <section className="li-lobby-participant-presence" aria-label="People in the meeting">
                        <button
                          type="button"
                          className="li-lobby-participant-presence-trigger"
                          onClick={() => setLobbyPresenceExpanded((expanded) => !expanded)}
                          aria-expanded={lobbyPresenceExpanded}
                          aria-controls="lobby-participants-in-meeting"
                          aria-label={`${lobbyPresenceExpanded ? "Hide" : "Show"} people in the meeting`}
                          title={`${lobbyPresenceExpanded ? "Hide" : "Show"} people in the meeting`}
                        >
                          <span className="li-lobby-participant-avatars" aria-hidden>
                            {inMeetingLobbyParticipants.slice(0, 3).map((participant) => (
                              <span key={participant.identity} className="li-lobby-participant-avatar">
                                {getParticipantInitials(participant.displayName).slice(0, 2)}
                              </span>
                            ))}
                            {inMeetingLobbyParticipants.length > 3 ? (
                              <span className="li-lobby-participant-avatar is-more">
                                +{inMeetingLobbyParticipants.length - 3}
                              </span>
                            ) : null}
                          </span>
                          <span className="li-lobby-participant-presence-label">
                            {inMeetingLobbyParticipants.length} {inMeetingLobbyParticipants.length === 1 ? "participant" : "participants"} in meeting
                          </span>
                          <ChevronDown
                            size={16}
                            className={lobbyPresenceExpanded ? "is-expanded" : ""}
                            aria-hidden
                          />
                        </button>
                        {lobbyPresenceExpanded ? (
                          <ul id="lobby-participants-in-meeting" className="li-lobby-participant-list">
                            {inMeetingLobbyParticipants.map((participant) => (
                              <li key={participant.identity}>
                                <span className="li-lobby-participant-avatar" aria-hidden>
                                  {getParticipantInitials(participant.displayName).slice(0, 2)}
                                </span>
                                <span>{participant.displayName}</span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </section>
                    ) : null}

                    <section className="li-lobby-readiness" aria-label="Device status">
                      <ul className="li-lobby-readiness-list">
                        {lobbyReadinessItems.map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <li
                              key={item.key}
                              className={`li-lobby-readiness-item is-${item.tone}`}
                            >
                              <ItemIcon size={15} aria-hidden />
                              <span>{item.label}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </section>

                    {requiresJoinConsent ? (
                      <label className="li-lobby-consent">
                        <input
                          type="checkbox"
                          checked={recordingConsentAccepted}
                          onChange={(event) => {
                            setRecordingConsentAccepted(event.target.checked);
                            if (event.target.checked) {
                              setTokenError("");
                            }
                          }}
                        />
                        <span>
                          This interview may be recorded and a transcript may be saved when
                          enabled by the host. Recordings are retained for up to {retentionDays}{" "}
                          days. I agree to join with this notice.
                        </span>
                      </label>
                    ) : null}

                    {hideCandidateLobbyCta ? null : (
                    <button
                      type="button"
                      className="li-lobby-cta"
                      onClick={handleCandidateLobbyAction}
                      disabled={
                        loading ||
                        joining ||
                        candidateRequestBusy ||
                        !canTriggerLobbyPrimaryActionSafe ||
                        (interviewerTokenFromUrl &&
                          role !== "candidate" &&
                          interviewerTokenVerifying) ||
                        Boolean(interviewerTokenError)
                      }
                    >
                      <LogIn size={17} aria-hidden />
                      <span>
                        {candidateRequestBusy
                          ? "Sending request..."
                          : joining
                            ? "Joining..."
                            : interviewerTokenVerifying &&
                                role !== "candidate" &&
                                interviewerTokenFromUrl
                              ? "Verifying link..."
                              : canRejoinSession
                                ? lobbyRejoinCopy?.ctaLabel || "Rejoin Meeting"
                                : candidateJoinApproved
                                  ? "Join now"
                                  : role === "candidate" && candidateNeedsApproval
                                    ? "Request to Join"
                                    : "Join now"}
                      </span>
                    </button>
                    )}

                    {candidateWasRemoved ||
                    (candidateNeedsApproval && candidateLobbyNotice && !candidateInWaitingRoom) ? (
                      <div
                        className={`li-lobby-card-notice ${
                          candidateWasRemoved
                            ? "is-error"
                            : candidateJoinApproved
                              ? "is-success"
                              : ""
                        }`}
                      >
                        {candidateWasRemoved
                          ? REMOVED_FROM_INTERVIEW_MESSAGE
                          : candidateLobbyNotice}
                      </div>
                    ) : null}

                    {lobbyRejoinCopy && role !== "candidate" ? (
                      <div className="li-lobby-card-notice">{lobbyRejoinCopy.notice}</div>
                    ) : null}

                    {joinPolicyNotice ? (
                      <div className="li-lobby-card-notice">{joinPolicyNotice}</div>
                    ) : null}

                    {!livekitReady ? (
                      <div className="li-lobby-card-notice">
                        This interview room is not ready yet. Please try again shortly or contact the hiring team.
                      </div>
                    ) : null}

                    {interviewerTokenError ? (
                      <div className="li-lobby-card-error">
                        This interview link is no longer valid. Please use the latest link from your email.
                      </div>
                    ) : null}

                    {lobbyTokenError ? (
                      <div className="li-lobby-card-error">{lobbyTokenError}</div>
                    ) : null}
                  </div>

                  {/* Waiting candidates panel — only for host */}
                  {canManageJoinRequests ? (
                    <HostJoinRequests
                      inline
                      isOpen
                      requests={pendingJoinRequests}
                      busyRequestId={joinRequestBusyId}
                      error={joinRequestError}
                      onRespond={handleJoinRequestDecision}
                    />
                  ) : null}
                </aside>
              </div>
            )}
          </div>

        </section>

        {canShowPostMeetingFeedback ? (
          <>
            <InterviewerFeedbackChoiceModal
              open={feedbackChoiceOpen}
              busy={feedbackFlowBusy || endingMeeting}
              error={feedbackFlowError}
              onGiveNow={handleFeedbackGiveNow}
              onGiveLater={handleFeedbackGiveLater}
            />
            <InterviewerFeedbackWizard
              open={feedbackWizardOpen}
              token={feedbackAccessToken}
              actorEmail={currentUserEmail}
              onClose={() => {
                setFeedbackWizardOpen(false);
                setFeedbackChoiceOpen(true);
              }}
              onSubmitted={handleFeedbackSubmitted}
            />
          </>
        ) : null}
      </main>
    </div>
  );
};

export default RoomPage;
